import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./PropertyPostConfiguration.scss";
import clsx from "clsx";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import dayjs, { Dayjs } from "dayjs";
import toast from "react-hot-toast";
import { Modal } from "@mui/material";
import { PropertyApi } from "../../../../../utils/api/Property";
import { CommonEnums } from "../../../../../enums/common.enums";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import CustomPageLoader from "../../../../../components/loaders/CustomPageLoader";
import FacebookWhiteIcon from "../../../../../components/icons/FacebookWhiteIcon";
import FacebookIcon from "../../../../../components/icons/FacebookIcon";
import InstagramWhiteIcon from "../../../../../components/icons/InstagramWhiteIcon";
import InstagramIcon from "../../../../../components/icons/InstagramIcon";
import GoogleBusinessWhiteIcon from "../../../../../components/icons/GoogleBusinessWhiteIcon";
import GoogleBusinessIcon from "../../../../../components/icons/GoogleBusinessIcon";
import LinkedInWhiteIcon from "../../../../../components/icons/LinkedInWhiteIcon";
import LinkedInIcon from "../../../../../components/icons/LinkedInIcon";
import XWhiteIcon from "../../../../../components/icons/XWhiteIcon";
import XIcon from "../../../../../components/icons/XIcon";
import TikTokWhiteIcon from "../../../../../components/icons/TikTokWhiteIcon";
import TikTokIcon from "../../../../../components/icons/TikTokIcon";
import LockIcon2 from "../../../../../components/icons/LockIcon2";
import YouTubeWhiteIcon from "../../../../../components/icons/YouTubeWhiteIcon";
import YouTubeIcon from "../../../../../components/icons/YouTubeIcon";
import {
  getAddress,
  getAgencyLogoForDesignTool,
  getAgencyLogoUrl,
  getAgentProfileImageUrl,
  getCdnImageUrl,
  getDesignTemplateUrl,
  getFontUrl,
  getImageAssetUrl,
  getImageUrl,
  loadImageAssetForDesignTool,
} from "../../../../../utils/helpers/common";
import LeftArrowIcon from "../../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../../components/icons/RightArrowIcon";
import CustomizeImageIcon from "../../../../../components/icons/CustomizeImageIcon";
import EditCaptionIcon from "../../../../../components/icons/EditCaptionIcon";
import PreviewCircleIcon from "../../../../../components/icons/PreviewCircleIcon";
import DownloadIcon from "../../../../../components/icons/DownloadIcon";
import TrueIcon from "../../../../../components/icons/TrueIcon";
import PropertyPostSystemMenuIcon from "./PropertyPostSystemMenuIcon";
import PropertyPlatformPostMenuIcon from "./PropertyPlatformPostMenuIcon";
import FacebookColorIcon from "../../../../../components/icons/FacebookColorIcon";
import InstagramColorIcon from "../../../../../components/icons/InstagramColorIcon";
import GoogleBusinessColorIcon from "../../../../../components/icons/GoogleBusinessColorIcon";
import LinkedInColorIcon from "../../../../../components/icons/LinkedInColorIcon";
import { PostPreferencesApi } from "../../../../../utils/api/PostPreferences";
import PlatformIssueCard from "./PlatformIssueCard";
import PropertyStagesDesignData from "../../../../../components/image.tool/property.stages.design.data";
import axiosInstance from "../../../../../utils/api/Axios";
import { CommonUtils } from "../../../../../utils/helpers/common.utils";
import { LocalStorageHelpers } from "../../../../../utils/helpers/localstorage.helpers";
import { AgentData } from "../../../../../@types/agent.data";
import AgencyData from "../../../../../@types/agency.data";
import { TemplatesApi } from "../../../../../utils/api/Templates";
import { PropertyData } from "../../../../../@types/property.data";
import { AgencyDesignConfig } from "../../../../../@types/agency.design.config";
import { DesignData } from "../../../../../@types/design.data";
import { formatPrice } from "../../../../../utils/helpers/helpers";
import { StageConfigData } from "../../../../../@types/stage.config.data";
import DesignPreviewRenderer from "../../../../../components/image.tool/DesignPreviewRenderer";

import momentWithTimezone from "moment-timezone";
import PreviewModal from "../PreviewModal";
import ImagePreviewModal from "../ImagePreviewModal";
import PostHeader from "../PostHeader";
import DesignPendingModal from "../DesignPendingModal";
import SocialMediaNotConnectedModal from "../SocialMediaNotConnectedModal";
import { SocialMediaApi } from "../../../../../utils/api/SocialMedia";
import SocialMediaWarningIcon from "../../../../../components/icons/SocialMediaWarningIcon";
import SavingProgressModal from "../SavingProgressModal";

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;

/**
 * Design stages - property marketing for sale
 */
let designStagesForSalePropertyMarketing = [
  {
    label: "Coming Soon - For Sale",
    stage_slug: "coming_soon_property_marketing_for_sale",

    /**
     * Possible post statuses are:
     * pending | draft | schedule_for_upload | issues_detected | completed
     */
    post_status: "pending",

    /**
     * Save the latest post for this stage here if any
     */
    latest_post: null,

    is_stage_skipped: false,
  },
  {
    label: "For Sale / Just Listed",
    stage_slug: "for_sale_listing_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Auction",
    stage_slug: "auction_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Open Home",
    stage_slug: "open_home_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Under Offer / Contract",
    stage_slug: "under_offer_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
];

/**
 * Design stages - property marketing for rent
 */
let designStagesForRentPropertyMarketing = [
  {
    label: "Coming Soon - For Rent",
    stage_slug: "coming_soon_property_marketing_for_rent",

    /**
     * Possible post statuses are:
     * pending | draft | schedule_for_upload | issues_detected | completed
     */
    post_status: "pending",

    /**
     * Save the latest post for this stage here if any
     */
    latest_post: null,

    is_stage_skipped: false,
  },
  {
    label: "For Rent Listing",
    stage_slug: "for_rent_listing_property_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Open Home",
    stage_slug: "open_home_property_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Deposit taken",
    stage_slug: "deposit_taken_property_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
];

const PropertyPostJourneyConfiguration = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { propertyPostId } = useParams<{ propertyPostId: string }>();
  const designStageFromParams = searchParams.get("template_type");
  const [propertyPost, setPropertyPost] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState("Facebook");
  const [sameTextToggle, setSameTextToggle] = useState(false);
  const [postPreviewIndex, setPostPreviewIndex] = useState(0);
  const [postStoryPreviewIndex, setPostStoryPreviewIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [localPostStatus, setLocalPostStatus] = useState<
    "draft" | "scheduled" | "published"
  >("draft");
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [date, setDate] = useState<string>("");
  const [timeTo, setTimeTo] = useState<string>("");
  const [publishedDateTime, setPublishedDateTime] = useState<Dayjs | null>(
    null,
  );
  const [scheduledDateTime, setScheduledDateTime] = useState<Dayjs | null>(
    null,
  );
  const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
  const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(null);
  const [scheduleMode, setScheduleMode] = useState<"schedule" | "auto" | null>(
    null,
  );
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  // const [postCaption, setPostCaption] = useState("");
  const scheduleFormRef = useRef<HTMLDivElement | null>(null);
  const [scheduleDateTime, setScheduleDateTime] = useState<dayjs.Dayjs | null>(
    null,
  );
  const lockedTabs = ["TikTok", "YouTube"];
  const isLocked = (tab: string) => lockedTabs.includes(tab);
  const stageLabelFromUrl = searchParams.get("stage_label");
  const navigate = useNavigate();

  const [postNowModalOpen, setPostNowModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [cancelScheduleModalOpen, setCancelScheduleModalOpen] = useState(false);
  const [editScheduleModalOpen, setEditScheduleModalOpen] = useState(false);
  const [timelineDocs, setTimelineDocs] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [isFloatingPublishOpen, setIsFloatingPublishOpen] = useState(true);
  type ConfirmAction = "cancel" | "restore" | "archive" | "unarchive";

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [menuLoading, setMenuLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );

  const [savePostPreferences, setSavePostPreferences] = useState(true);
  const [userPreferences, setUserPreferences] = useState<any>(null);
  const [postPreferencesModalOpen, setPostPreferencesModalOpen] =
    useState(false);
  const [sameCaptionForAll, setSameCaptionForAll] = useState(false);
  const [alwaysShareStory, setAlwaysShareStory] = useState(false);

  const currentStatus = propertyPost?.post_status;
  const isCancelled = currentStatus === "cancelled";
  const isArchived = currentStatus === "archived";

  const imagePreviewRef = useRef<HTMLDivElement | null>(null);
  const storyPreviewRef = useRef<HTMLDivElement | null>(null);
  const [previewImages, setPreviewImages] = useState<string[]>([]);

  const [notConnectedModalOpen, setNotConnectedModalOpen] = useState(false);
  const [notConnectedPlatform, setNotConnectedPlatform] = useState("Facebook");

  const [savingModalOpen, setSavingModalOpen] = useState(false);
  const [savingAction, setSavingAction] = useState<
    "post_now" | "schedule" | "save_changes" | null
  >(null);

  // const designStageFromParams = useMemo(() => {
  //   if (propertyPost) {
  //     return propertyPost.template_type || propertyPost.design_stage || null;
  //   }
  //   return null;
  // }, [propertyPost]);

  console.log("designStageFromParams", designStageFromParams);

  const [selectedPlatforms, setSelectedPlatforms] = useState<
    Record<string, boolean>
  >({
    Facebook: true,
    Instagram: true,
    google_business: true,
    LinkedIn: true,
    TikTok: false,
    YouTube: false,
  });

  const [connectedPlatforms, setConnectedPlatforms] = useState<
    Record<string, boolean>
  >({
    Facebook: false,
    Instagram: false,
    google_business: false,
    LinkedIn: false,
  });

  type PlatformKey =
    | "facebook"
    | "instagram"
    | "google_business"
    | "linkedin"
    | "tiktok"
    | "youtube";

  const PLATFORM_LABEL: Record<PlatformKey, string> = {
    facebook: "Facebook",
    instagram: "Instagram",
    google_business: "Google",
    linkedin: "LinkedIn",
    tiktok: "TikTok",
    youtube: "YouTube",
  };

  const isFailed = (s?: string) => s === "failed" || s === "error";
  const isWarning = (s?: string) =>
    s === "processing" || s === "pending_for_social_media_upload";

  const normalizeMsg = (issue: any) => {
    if (!issue) return null;
    if (typeof issue === "string") return { title: issue, message: "" };
    return {
      title: issue?.title || issue?.reason || issue?.code || "",
      message: issue?.message || issue?.detail || issue?.description || "",
    };
  };

  type SocialPlatformKey =
    | "facebook"
    | "instagram"
    | "google_business"
    | "linkedin";

  const getPlatformKeyFromTab = (tab: string): SocialPlatformKey | null => {
    if (tab === "Facebook") return "facebook";
    if (tab === "Instagram") return "instagram";
    if (tab === "google_business") return "google_business";
    if (tab === "LinkedIn") return "linkedin";
    return null;
  };

  const [platformPostNowOpen, setPlatformPostNowOpen] = useState(false);
  const [platformScheduleOpen, setPlatformScheduleOpen] = useState(false);

  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [isStoryPreviewOpen, setIsStoryPreviewOpen] = useState(false);

  const [imagePreviewStartIndex, setImagePreviewStartIndex] = useState(0);

  const [previewImagesForModal, setPreviewImagesForModal] = useState<
    { medium: string; large: string }[]
  >([]);

  const [platformScheduleDate, setPlatformScheduleDate] =
    useState<Dayjs | null>(dayjs());
  const [platformScheduleTime, setPlatformScheduleTime] =
    useState<Dayjs | null>(dayjs());

  const activePlatformKey = getPlatformKeyFromTab(activeTab);
  const activePlatformLabel = activeTab;

  const [propertyImageIndex, setPropertyImageIndex] = useState(0);

  const [showDesignPendingModal, setShowDesignPendingModal] = useState(false);

  const propertyImages = useMemo(() => {
    const allImages = propertyPost?.property_id?.property_images || [];
    const thumbnailId = propertyPost?.property_id?.thumbnail_image_ref?._id;

    if (!thumbnailId) return allImages;

    return allImages.filter((img: any) => img._id !== thumbnailId);
  }, [propertyPost]);

  const orderedPropertyImages = useMemo(() => {
    const allImages = propertyPost?.property_id?.property_images || [];
    const thumbnail = propertyPost?.property_id?.thumbnail_image_ref;

    if (!thumbnail) return allImages;

    const filtered = allImages.filter((img: any) => img._id !== thumbnail._id);
    return [thumbnail, ...filtered];
  }, [propertyPost]);

  const [commonState, setCommonState] = useState({
    templateConfigs: null,
    loadingPostData: true,
    postData: null,
    loadingTemplateConfigs: true,
    agencyData: null,
    agentData: null,
    brandData: null,
    propertyData: null,
    designStage: "",
    useAgencyOfficeLogo: false,
    imageTemplateAssets: null,
    storyTemplateAssets: null,
    agencyFontsData: PropertyStagesDesignData.commonFontsData,
    downloadingFontsData: true,
    loadingFontsData: true,
    fontsToBeLoaded: [],
    imageDesignData: null,
    storyDesignData: null,
  });

  console.log("commonState: ", commonState);

  type SchedulePlatformKey =
    | "facebook"
    | "instagram"
    | "google_business"
    | "linkedin";

  const [sameScheduleAll, setSameScheduleAll] = useState(true);

  const SCHEDULE_PLATFORMS: Array<{
    key: SchedulePlatformKey;
    selectedKey: string;
    label: string;
    Icon: any;
    supported?: boolean;
    showNote?: boolean;
  }> = [
    {
      key: "facebook",
      selectedKey: "Facebook",
      label: "Facebook",
      Icon: FacebookColorIcon,
      supported: true,
      showNote: true,
    },
    {
      key: "instagram",
      selectedKey: "Instagram",
      label: "Instagram",
      Icon: InstagramColorIcon,
      supported: true,
      showNote: true,
    },
    {
      key: "google_business",
      selectedKey: "google_business",
      label: "Google Business",
      Icon: GoogleBusinessColorIcon,
      supported: true,
    },
    {
      key: "linkedin",
      selectedKey: "LinkedIn",
      label: "LinkedIn",
      Icon: LinkedInColorIcon,
      supported: true,
    },
  ];

  const [perPlatformSchedule, setPerPlatformSchedule] = useState<
    Record<SchedulePlatformKey, { date: Dayjs | null; time: Dayjs | null }>
  >({
    facebook: { date: dayjs(), time: dayjs() },
    instagram: { date: dayjs(), time: dayjs() },
    google_business: { date: dayjs(), time: dayjs() },
    linkedin: { date: dayjs(), time: dayjs() },
  });

  const getPlatformKeyFromSelectedLabel = (
    label: string,
  ): SchedulePlatformKey | null => {
    if (label === "Facebook") return "facebook";
    if (label === "Instagram") return "instagram";
    if (label === "google_business") return "google_business";
    if (label === "LinkedIn") return "linkedin";
    return null;
  };

  const togglePlatformSelected = async (platformLabel: string) => {
    if (isLocked(platformLabel)) return;
    if (!propertyPostId) return;

    const platformKey = getPlatformKeyFromSelectedLabel(platformLabel);
    if (!platformKey) return;

    const nextEnabled = !selectedPlatforms[platformLabel];

    setSelectedPlatforms((p) => ({
      ...p,
      [platformLabel]: nextEnabled,
    }));

    // try {
    //   await PropertyApi.updatePropertyPost(propertyPostId, {
    //     platform: platformKey,
    //     enabled: nextEnabled,
    //   });

    //   await fetchPost(true);
    //   await fetchTimeline();
    // } catch (e) {
    //   console.error(e);
    //   toast.error("Failed to update platform selection");

    //   setSelectedPlatforms((p) => ({
    //     ...p,
    //     [platformLabel]: !nextEnabled,
    //   }));
    // }
  };

  const combineDayjsDateTime = (d: Dayjs | null, t: Dayjs | null) => {
    if (!d || !t) return null;
    const combined = d
      .hour(t.hour())
      .minute(t.minute())
      .second(0)
      .millisecond(0);
    return combined.isValid() ? combined : null;
  };

  const getSelectedSupportedPlatforms = (): SchedulePlatformKey[] => {
    const platforms: SchedulePlatformKey[] = [];

    if (selectedPlatforms["Facebook"]) platforms.push("facebook");
    if (selectedPlatforms["Instagram"]) platforms.push("instagram");
    if (selectedPlatforms["google_business"]) platforms.push("google_business");
    if (selectedPlatforms["LinkedIn"]) platforms.push("linkedin");

    return platforms;
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>) =>
    setMenuAnchorEl(e.currentTarget);
  const closeMenu = () => setMenuAnchorEl(null);

  const openConfirm = (action: ConfirmAction) => {
    closeMenu();
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (menuLoading) return;
    setConfirmOpen(false);
    setConfirmAction(null);
  };

  const tabs = [
    "Facebook",
    "Instagram",
    "Google Business",
    "Linkedin",
    "Youtube",
  ];

  const images = [
    "/images/property-inside.jpg",
    "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
    "https://as2.ftcdn.net/jpg/02/61/89/29/1000_F_261892957_6jyBXvEgM79iYr1eEiJKCosnVPJdvHHr.jpg",
  ];

  const isScheduleUiOpenRef = useRef(false);

  useEffect(() => {
    isScheduleUiOpenRef.current = scheduleModalOpen || editScheduleModalOpen;
  }, [scheduleModalOpen, editScheduleModalOpen]);

  const getStageLabel = useMemo(() => {
    const stageSlug = propertyPost?.design_stage || propertyPost?.template_type;
    if (!stageSlug) return "-";

    const listingType =
      propertyPost?.property_id?.listing_type?.listing_type || "sale";

    console.log("property details", propertyPost?.property_id);
    const stages =
      listingType === "rent"
        ? designStagesForRentPropertyMarketing
        : designStagesForSalePropertyMarketing;
    const matched = stages.find((s) => s.stage_slug === stageSlug);
    return matched ? matched.label : stageSlug;
  }, [propertyPost]);

  const findStageConfig = ({
    listingType,
    stageType,
    stageData,
  }: {
    listingType: string;
    stageType: string;
    stageData: any;
  }): StageConfigData | null => {
    if (!stageData || !stageData?.length) return null;

    for (const item of stageData) {
      if (
        item?.stage_id?.listing_type === listingType &&
        item?.stage_id?.stage_type === stageType
      ) {
        return item;
      }
    }

    return null;
  };

  const getFontVariants = async (type: string, mainFontId: string) => {
    const res = await axiosInstance.get(`/${type}/variants/for-main-font`, {
      params: { main_font_id: mainFontId },
    });
    return res.data?.data?.docs || [];
  };

  const getFontKeys = (fonts: any, postscriptName: string) => {
    const matches = [];
    for (const textType in fonts) {
      for (const key in fonts[textType]) {
        if (fonts[textType][key].postscript_name === postscriptName) {
          matches.push({ text_type: textType, key });
        }
      }
    }
    return matches;
  };

  const preloadFonts = async ({ fontsToBeLoaded, agencyFontsData }: any) => {
    const loadedFonts = [];

    const fonts = fontsToBeLoaded;

    setCommonState((prev) => ({
      ...prev,
      loadingFontsData: true,
      downloadingFontsData: false,
      fontsToBeLoaded: fontsToBeLoaded,
      agencyFontsData: agencyFontsData,
    }));

    let updatedFontsData = CommonUtils.cloneDeep(agencyFontsData);

    // console.log("preloadFonts >> updatedFontsData: ", updatedFontsData);
    console.log("preloadFonts >> fonts: ", fonts);

    await Promise.all(
      fonts.map(async (font: any) => {
        let loadFont = font.load_font;

        console.log("preloadFonts >> loadFont: ", loadFont);

        if (!loadFont) return;

        let fontKeys = getFontKeys(updatedFontsData, font.postscript_name);
        let fontUrl = getFontUrl(font);

        // console.log(
        //   "preloadFonts >> font.postscript_name: ",
        //   font.postscript_name,
        // );
        // console.log("preloadFonts >> fontKeys: ", fontKeys);

        try {
          const fontFace = new FontFace(
            font.postscript_name,
            `url(${fontUrl})`,
          );

          await fontFace.load();
          document.fonts.add(fontFace);
          loadedFonts.push(fontFace);

          // debugger

          fontKeys.forEach(({ text_type, key }) => {
            // console.log("text_type: ", text_type, " > key: ", key);
            updatedFontsData[text_type][key].loaded = true;
            updatedFontsData[text_type][key].error = false;
          });
        } catch (error) {
          console.error(`Failed to load font: ${font.postscript_name}`, error);

          fontKeys.forEach(({ text_type, key }) => {
            updatedFontsData[text_type][key].loaded = false;
            updatedFontsData[text_type][key].error = true;
          });
        }
      }),
    );

    // console.log("preloadFonts >> updatedFontsData: ", updatedFontsData);
    // await new Promise((resolve) => setTimeout(resolve, 150000));

    let imageDesignData = CommonUtils.cloneDeep(
      commonState.imageDesignData as any,
    );
    let storyDesignData = CommonUtils.cloneDeep(
      commonState.storyDesignData as any,
    );

    imageDesignData.fonts = updatedFontsData;
    storyDesignData.fonts = updatedFontsData;

    setCommonState((prev) => ({
      ...prev,
      loadingFontsData: false,
      downloadingFontsData: false,
      agencyFontsData: updatedFontsData,
      imageDesignData,
      storyDesignData,
    }));
  };

  const getSystemFontVariantsForMainFont = async ({
    mainFontId,
  }: {
    mainFontId: string;
  }) => {
    try {
      const res = await axiosInstance.get(
        `/system-fonts/variants/for-main-font`,
        {
          params: {
            main_font_id: mainFontId,
          },
        },
      );

      return {
        success: true,
        data: res.data?.data?.docs,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching brand settings!",
        data: err?.response?.data,
      };
    }
  };

  const getBrandFontVariantsForMainFont = async ({
    mainFontId,
  }: {
    mainFontId: string;
  }) => {
    try {
      const res = await axiosInstance.get(
        `/brand-fonts/variants/for-main-font`,
        {
          params: {
            main_font_id: mainFontId,
          },
        },
      );

      return {
        success: true,
        data: res.data?.data?.docs,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching brand settings!",
        data: err?.response?.data,
      };
    }
  };

  const getAgencyFontVariantsForMainFont = async ({
    mainFontId,
  }: {
    mainFontId: string;
  }) => {
    try {
      const res = await axiosInstance.get(
        `/agency-fonts/variants/for-main-font`,
        {
          params: {
            main_font_id: mainFontId,
          },
        },
      );

      return {
        success: true,
        data: res.data?.data?.docs,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching brand settings!",
        data: err?.response?.data,
      };
    }
  };

  const fetchFontsData = async () => {
    setCommonState((prev) => ({
      ...prev,
      downloadingFontsData: true,
      loading: true,
    }));

    let agencyData = commonState.agencyData as any;
    let brandData = commonState.brandData as any;

    let isBrandAgency = false;
    if (agencyData?.brand_id) isBrandAgency = true;

    let settingsData = null;
    if (isBrandAgency) {
      settingsData = brandData?.settings;
    } else settingsData = agencyData?.settings;

    let titleFont = settingsData?.title_fonts;
    let subtitleFont = settingsData?.subtitle_fonts;
    let bodyFont = settingsData?.body_text_fonts;

    console.log("subtitleFont: ", subtitleFont);

    let titleFontSelectedFrom = settingsData?.title_fonts_selected_from;
    let subtitleFontSelectedFrom = settingsData?.subtitle_fonts_selected_from;
    let bodyFontSelectedFrom = settingsData?.body_text_fonts_selected_from;

    // if (selectedFonts && receiveFontChanges) {
    //   if (
    //     selectedFonts?.title?.main_font_id &&
    //     selectedFonts?.title?.main_font_id != "design_default"
    //   ) {
    //     titleFont = selectedFonts?.title?.main_font_id;
    //     titleFontSelectedFrom = selectedFonts.title.selected_from;
    //   }

    //   if (
    //     selectedFonts?.subtitle?.main_font_id &&
    //     selectedFonts?.subtitle?.main_font_id != "design_default"
    //   ) {
    //     subtitleFont = selectedFonts?.subtitle?.main_font_id;
    //     subtitleFontSelectedFrom = selectedFonts.subtitle.selected_from;
    //   }

    //   if (
    //     selectedFonts?.body?.main_font_id &&
    //     selectedFonts?.body?.main_font_id != "design_default"
    //   ) {
    //     bodyFont = selectedFonts?.body?.main_font_id;
    //     bodyFontSelectedFrom = selectedFonts.body.selected_from;
    //   }
    // }

    let fontsPromises = [];

    const processFontRequest = async ({
      fontType,
      mainFontId,
      fontVariant,
    }: {
      fontType: string;
      mainFontId: string;
      fontVariant: string;
    }) => {
      let fontsData = [];

      if (fontType === "system-fonts") {
        let res = await getSystemFontVariantsForMainFont({ mainFontId });
        if (res?.success && res.data) fontsData = res.data;
      }

      if (fontType === "brand-fonts") {
        let res = await getBrandFontVariantsForMainFont({ mainFontId });
        if (res?.success && res.data) fontsData = res.data;
      }

      if (fontType === "agency-fonts") {
        let res = await getAgencyFontVariantsForMainFont({ mainFontId });
        if (res?.success && res.data) fontsData = res.data;
      }

      return { fonts: fontsData, fontVariant };
    };

    if (titleFont && titleFont != "design_default") {
      if (titleFontSelectedFrom === "system-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "system-fonts",
            mainFontId: titleFont,
            fontVariant: "title_font",
          }),
        );
      }
      if (titleFontSelectedFrom === "brand-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "brand-fonts",
            mainFontId: titleFont,
            fontVariant: "title_font",
          }),
        );
      }
      if (titleFontSelectedFrom === "agency-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "agency-fonts",
            mainFontId: titleFont,
            fontVariant: "title_font",
          }),
        );
      }
    }

    if (subtitleFont && subtitleFont != "design_default") {
      if (subtitleFontSelectedFrom === "system-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "system-fonts",
            mainFontId: subtitleFont,
            fontVariant: "subtitle_font",
          }),
        );
      }
      if (subtitleFontSelectedFrom === "brand-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "brand-fonts",
            mainFontId: subtitleFont,
            fontVariant: "subtitle_font",
          }),
        );
      }
      if (subtitleFontSelectedFrom === "agency-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "agency-fonts",
            mainFontId: subtitleFont,
            fontVariant: "subtitle_font",
          }),
        );
      }
    }

    if (bodyFont && bodyFont != "design_default") {
      if (bodyFontSelectedFrom === "system-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "system-fonts",
            mainFontId: bodyFont,
            fontVariant: "body_font",
          }),
        );
      }
      if (bodyFontSelectedFrom === "brand-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "brand-fonts",
            mainFontId: bodyFont,
            fontVariant: "body_font",
          }),
        );
      }
      if (bodyFontSelectedFrom === "agency-fonts") {
        fontsPromises.push(() =>
          processFontRequest({
            fontType: "agency-fonts",
            mainFontId: bodyFont,
            fontVariant: "body_font",
          }),
        );
      }
    }

    let fontsResults = await Promise.all(fontsPromises.map((task) => task()));

    let titleFonts = [];
    let subtitleFonts = [];
    let bodyFonts = [];

    for (const item of fontsResults) {
      if (item.fontVariant === "title_font") titleFonts.push(item.fonts);
      if (item.fontVariant === "subtitle_font") subtitleFonts.push(item.fonts);
      if (item.fontVariant === "body_font") bodyFonts.push(item.fonts);
    }

    titleFonts = titleFonts.flat();
    subtitleFonts = subtitleFonts.flat();
    bodyFonts = bodyFonts.flat();

    function uniqueById(arr: any) {
      const result = [];
      const seen = new Set();
      for (const item of arr) {
        if (!seen.has(item._id)) {
          seen.add(item._id);
          result.push(item);
        }
      }
      return result;
    }

    // if (titleFont == "design_default" || !titleFontSelectedFrom) {
    //   if (designTemplateData?.title_fonts) {
    //     titleFonts = titleFonts.concat(designTemplateData?.title_fonts);
    //   }
    // }

    // if (subtitleFont == "design_default" || !subtitleFontSelectedFrom) {
    //   if (designTemplateData?.subtitle_fonts) {
    //     subtitleFonts = subtitleFonts.concat(
    //       designTemplateData?.subtitle_fonts,
    //     );
    //   }
    // }

    // if (bodyFont == "design_default" || !bodyFontSelectedFrom) {
    //   if (designTemplateData?.body_fonts) {
    //     bodyFonts = bodyFonts.concat(designTemplateData?.body_fonts);
    //   }
    // }

    let updatedTitleFonts = uniqueById(titleFonts) as any;
    let updatedSubtitleFonts = uniqueById(subtitleFonts) as any;
    let updatedBodyFonts = uniqueById(bodyFonts) as any;

    let designFontsData = commonState.agencyFontsData as any;

    for (const [index, element] of updatedTitleFonts.entries()) {
      let weight = element.font_weight;
      let isItalic = element.is_italic;
      let fontKey = `font_${weight}`;

      // let requiredTitleFontWeights =
      //   designTemplateData?.required_font_weights?.title ?? [];
      // requiredTitleFontWeights = requiredTitleFontWeights.map((_weight) =>
      //   String(_weight),
      // );

      let loadFont = true;
      let weightIdentifier = String(weight);

      if (isItalic) {
        weightIdentifier = `${weight}_italic`;
        fontKey = `${fontKey}_italic`;
      }

      // if (requiredTitleFontWeights.includes(weightIdentifier)) {
      //   loadFont = true;
      // }

      updatedTitleFonts[index].load_font = loadFont;

      if (designFontsData.title?.[fontKey]) {
        designFontsData.title[fontKey] = {
          loaded: false,
          error: false,
          postscript_name: element.postscript_name,
          load_font: loadFont,
        };
      }
    }

    for (const [index, element] of updatedSubtitleFonts.entries()) {
      let weight = element.font_weight;
      let isItalic = element.is_italic;
      let fontKey = `font_${weight}`;

      // let requiredSubtitleFontWeights =
      //   designTemplateData?.required_font_weights?.subtitle ?? [];
      // requiredSubtitleFontWeights = requiredSubtitleFontWeights.map((_weight) =>
      //   String(_weight),
      // );

      let loadFont = true;
      let weightIdentifier = String(weight);

      if (isItalic) {
        weightIdentifier = `${weight}_italic`;
        fontKey = `${fontKey}_italic`;
      }

      // if (requiredSubtitleFontWeights.includes(weightIdentifier)) {
      //   loadFont = true;
      // }

      updatedSubtitleFonts[index].load_font = loadFont;

      if (designFontsData.subtitle?.[fontKey]) {
        designFontsData.subtitle[fontKey] = {
          loaded: false,
          error: false,
          postscript_name: element.postscript_name,
          load_font: loadFont,
        };
      }
    }

    for (const [index, element] of updatedBodyFonts.entries()) {
      let weight = element.font_weight;
      let isItalic = element.is_italic;
      let fontKey = `font_${weight}`;

      if (isItalic) fontKey = `${fontKey}_italic`;

      // let requiredBodyFontWeights =
      //   designTemplateData?.required_font_weights?.body ?? [];
      // requiredBodyFontWeights = requiredBodyFontWeights.map((_weight) =>
      //   String(_weight),
      // );

      let loadFont = true;
      let weightIdentifier = String(weight);

      if (isItalic) {
        weightIdentifier = `${weight}_italic`;
        fontKey = `${fontKey}_italic`;
      }

      // if (requiredBodyFontWeights.includes(weightIdentifier)) {
      //   loadFont = true;
      // }

      updatedBodyFonts[index].load_font = loadFont;

      if (designFontsData.body?.[fontKey]) {
        designFontsData.body[fontKey] = {
          loaded: false,
          error: false,
          postscript_name: element.postscript_name,
          load_font: loadFont,
        };
      }
    }

    let allFonts = [
      ...updatedTitleFonts,
      ...updatedSubtitleFonts,
      ...updatedBodyFonts,
    ];

    allFonts = uniqueById(allFonts);

    // setDesignData((prev) => ({
    //   ...prev,
    //   fonts: designFontsData,
    // }));

    // setState((prev) => ({
    //   ...prev,
    //   downloadingFontsData: false,
    //   fontsToBeLoaded: allFonts,
    //   agencyFontsData: designFontsData,
    //   loadingFontsData: true,
    // }));

    preloadFonts({
      agencyFontsData: designFontsData,
      fontsToBeLoaded: allFonts,
    });
  };

  const fetchPost = useCallback(
    async (isInitial = false) => {
      if (!propertyPostId) return;

      try {
        if (isInitial) setLoading(true);
        let agentDataFromLocalStorage =
          LocalStorageHelpers.getAgentData() as unknown as AgentData;
        let agencyDataFromLocalStorage =
          LocalStorageHelpers.getAgencyData() as unknown as AgencyData;

        // let agencyId = agencyDataFromLocalStorage?._id;

        // if (agencyId) agencyId = agentDataFromLocalStorage?.agency_ref?._id;

        let agencyId =
          agentDataFromLocalStorage?.agency_ref?._id ||
          agencyDataFromLocalStorage?._id ||
          null;

        setCommonState((prevState) => ({
          ...prevState,
          loadingPostData: true,
          loadingTemplateConfigs: true,
        }));

        let stagePrams = [designStageFromParams?.trim()] as any;

        const stageConfigsPromise = agencyId
          ? PropertyApi.getSavedStageConfigs(agencyId)
          : Promise.resolve({ data: { data: [] } } as any);

        const [res, designTemplateConfigRes, agencyStageConfigRes] =
          await Promise.all([
            PropertyApi.getPropertyPostById(propertyPostId),
            TemplatesApi.getDesignTemplateConfigForDesignSystem(stagePrams),
            stageConfigsPromise,
          ]);

        console.log("agencyStageConfigRes: ", agencyStageConfigRes);

        let designTemplateConfigData =
          designTemplateConfigRes?.data?.data ?? null;

        const isConfigComplete = designTemplateConfigData?.is_config_complete;

        // if (isConfigComplete === false) {
        //   setShowDesignPendingModal(true);
        // }

        if (isConfigComplete === false) {
          setShowDesignPendingModal(true);
          setCommonState((prevState) => ({
            ...prevState,
            loadingPostData: false,
            loadingTemplateConfigs: false,
          }));
          if (isInitial) setLoading(false);
          return;
        }

        let stageConfigsData = agencyStageConfigRes?.data?.data;

        if (!designTemplateConfigRes?.data?.success) {
          setCommonState((prevState) => ({
            ...prevState,
            loadingPostData: false,
            loadingTemplateConfigs: false,
          }));

          toast.error(
            "Something went wrong while fetching the design configs for the design system!",
          );
          return;
        }

        if (res?.data?.success) {
          const updatedPost = res.data.data;
          setPropertyPost(updatedPost);

          if (updatedPost.schedule_datetime && !isScheduleUiOpenRef.current) {
            const dt = dayjs(updatedPost.schedule_datetime);
            if (dt.isValid()) {
              setScheduleDateTime(dt);
              setScheduleDate(dt);
              setScheduleTime(dt);
            }
          }

          const updatedStatuses = [
            updatedPost?.image_post_design_activity_ref?.design_status,
            updatedPost?.story_post_design_activity_ref?.design_status,
            updatedPost?.video_post_design_activity_ref?.design_status,
            updatedPost?.reel_post_design_activity_ref?.design_status,
          ];

          const shouldPoll =
            updatedStatuses.includes("processing") ||
            !updatedStatuses.includes("pending_for_social_media_upload");

          if (shouldPoll && !intervalRef.current) {
            // intervalRef.current = setInterval(() => fetchPost(false), 3000);
          } else if (!shouldPoll && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          let useAgencyOfficeLogo =
            updatedPost?.agency_ref?.use_office_logo ?? false;

          let designStage = designStageFromParams?.trim() as string;
          let agencyData = updatedPost?.agency_ref as AgencyData;
          let brandData = updatedPost?.agency_ref?.brand_id ?? null;
          let propertyData = updatedPost?.property_id as PropertyData;
          let templateConfigData = designTemplateConfigData[
            designStageFromParams?.trim() as any
          ] as AgencyDesignConfig;

          let propertyCoAgentData = propertyData?.co_agent_ref;
          let propertyAgentData = propertyData?.agent_ref;

          /**
           * Create design data
           */

          let designData = (PropertyStagesDesignData as any)[
            designStage
          ] as DesignData;

          let imageDesignData = CommonUtils.cloneDeep(designData) as DesignData;

          let listingType = propertyData?.listing_type?.listing_type;

          let areaUnit = (propertyData.land_size_unit as any).slug;

          /**
           * Get agency timezone
           */

          let agencyTimezone = agencyData?.timezone;

          let eoiDateString = "";

          if (propertyData?.eoi_datetime) {
            let EOIDateMoment = momentWithTimezone.utc(
              propertyData.eoi_datetime,
            );

            eoiDateString = EOIDateMoment.clone()
              .tz(agencyTimezone)
              .format("DD.MM.YYYY, h:mm A");
          }

          console.log("eoiDateString: ", eoiDateString);

          imageDesignData.eoi_datetime = eoiDateString;

          //       // EOI date
          // let eoiDateString = ''

          // if (propertyRecord.eoi_datetime) {
          //   let eoiDatetime = moment(propertyRecord.eoi_datetime)
          //   eoiDateString = eoiDatetime.format('DD.MM.YYYY, hA')
          // }

          // Property info
          let bedroomCount = propertyData.bedrooms;
          let bathCount = propertyData.bathrooms;
          let parkingCount = propertyData.garage;
          let livingCount = propertyData.living;
          let propertyArea = propertyData.land_area as any;

          if (areaUnit == CommonEnums.landSizeUnits.hectare) {
            propertyArea = `${propertyArea} ha`;
          }

          if (areaUnit == CommonEnums.landSizeUnits.squareMeter) {
            propertyArea = `${propertyArea} m²`;
          }

          if (areaUnit == CommonEnums.landSizeUnits.acre) {
            propertyArea = `${propertyArea} ac`;
          }

          if (areaUnit == CommonEnums.landSizeUnits.squareFeet) {
            propertyArea = `${propertyArea} sqft`;
          }

          imageDesignData.bedroom_count = bedroomCount;
          imageDesignData.bathroom_count = bathCount;
          imageDesignData.parking_count = parkingCount;
          imageDesignData.living_count = livingCount as any;
          imageDesignData.property_area = propertyArea as any;

          /**
           * Property main image
           */
          imageDesignData.design_main_image = {
            /**
             * TODOS:
             * read original images to generate the post designs
             */
            url: loadImageAssetForDesignTool(propertyData.thumbnail_image_ref),
            load: true,
          };

          /**
           * Process agent data for design
           */

          if (propertyAgentData) {
            imageDesignData.agent_name =
              CommonUtils.getAgentName(propertyAgentData);
            imageDesignData.agent_email = propertyAgentData?.email;
            imageDesignData.agent_phone = CommonUtils.formatPhoneNumber({
              phoneNumber: propertyAgentData?.principal_agent_mobile_number,
            });
            imageDesignData.agent_profile_image_path = {
              url: loadImageAssetForDesignTool(
                propertyAgentData?.profile_image_ref,
              ),
              load: true,
            };
          }

          /**
           * Process co-agent data for design
           */
          if (propertyCoAgentData) {
            imageDesignData.co_agent_name =
              CommonUtils.getAgentName(propertyCoAgentData);
            imageDesignData.co_agent_email = propertyCoAgentData?.email;

            imageDesignData.co_agent_phone = CommonUtils.formatPhoneNumber({
              phoneNumber: propertyCoAgentData?.principal_agent_mobile_number,
            });

            imageDesignData.co_agent_profile_image_path = {
              url: loadImageAssetForDesignTool(
                propertyCoAgentData?.profile_image_ref,
              ),
              load: true,
            };
          } else {
            imageDesignData.co_agent_name = "";
            imageDesignData.co_agent_email = "";
            imageDesignData.co_agent_phone = "";
            imageDesignData.co_agent_profile_image_path = null;
          }

          /**
           * Process property data for design i.e., property address, prices, etc
           */

          let propertyPriceInfo = propertyData.price_type;

          let priceType = propertyPriceInfo?.slug ?? "";

          console.log("priceType: ", priceType);
          let startPrice = formatPrice(propertyData?.start_price);
          let endPrice = formatPrice(propertyData?.end_price);
          let propertyPrice = formatPrice(propertyData?.price);
          let bondPrice = formatPrice(propertyData?.bond_price);

          imageDesignData.property_price_type = priceType;
          imageDesignData.start_price = startPrice;
          imageDesignData.end_price = endPrice;
          imageDesignData.property_price = propertyPrice;
          imageDesignData.bond_price = bondPrice;

          imageDesignData.property_address = getAddress({
            unit: propertyData.unit,
            city: propertyData.city,
            country: propertyData.country,
            no: propertyData.property_no,
            streetName: propertyData.property_no_and_street_name,
            postCode: propertyData.post_code,
            state: propertyData.state,
          });

          console.log(
            "imageDesignData.property_address: ",
            imageDesignData.property_address,
          );

          /**
           * Update the design stage name as per the agency stage config
           */

          let propertyStageType = designData.property_stage_type;

          let listingTypeForStageData = "sell";
          let designListingType = "for_sale";
          let transactionTypeText = "For Sale";
          imageDesignData.listing_type = "for_sale";

          if (listingType == "rent") {
            listingTypeForStageData = "rent";
            designListingType = "for_rent";
            transactionTypeText = "For Rent";
          }

          imageDesignData.listing_type = designListingType as any;
          imageDesignData.transaction_type_text = transactionTypeText as any;

          let customStageConfig = findStageConfig({
            listingType: listingTypeForStageData,
            stageData: stageConfigsData,
            stageType: propertyStageType,
          });

          let designStageTitle = designData?.property_stage;

          if (customStageConfig?.display_title_id?.title)
            designStageTitle = customStageConfig?.display_title_id?.title;

          imageDesignData.property_stage = designStageTitle;

          imageDesignData.agency_logo = {
            url: getAgencyLogoForDesignTool({
              agencyData,
              brandData,
              useAgencyOfficeLogo,
            }),
            load: true,
          };

          imageDesignData.agency_trading_name =
            agencyData?.agency_trading_name ?? "";
          imageDesignData.agency_address = getAddress({
            unit: agencyData?.address_unit_number ?? "",
            city: agencyData?.city ?? "",
            country: agencyData?.country ?? "",
            no: agencyData?.house_number ?? "",
            postCode: agencyData?.post_code ?? "",
            state: agencyData?.state ?? "",
            streetName: agencyData?.street_name ?? "",
          });

          if (agencyData?.website) {
            imageDesignData.agency_website =
              CommonUtils.formatWebsiteLinkForPreview({
                website: agencyData.website,
              });
          }

          /**
           * TODO:
           * Load agency fonts data here
           */
          // imageDesignData.fonts = { ...agencyFontsData };

          const getColorsData = () => {
            const data = {
              base_color_primary: { hex: "" },
              base_color_secondary: { hex: "" },
              text_color_primary: { hex: "" },
              text_color_secondary: { hex: "" },
            };

            if (brandData && !useAgencyOfficeLogo) {
              data.base_color_primary.hex =
                brandData?.base_color_primary?.hex ?? "";
              data.base_color_secondary.hex =
                brandData?.base_color_secondary?.hex ?? "";
              data.text_color_primary.hex =
                brandData?.text_color_primary?.hex ?? "";
              data.text_color_secondary.hex =
                brandData?.text_color_secondary?.hex ?? "";
              return data;
            }

            data.base_color_primary.hex =
              agencyData?.base_color_primary?.hex ?? "";
            data.base_color_secondary.hex =
              agencyData?.base_color_secondary?.hex ?? "";
            data.text_color_primary.hex =
              agencyData?.text_color_primary?.hex ?? "";
            data.text_color_secondary.hex =
              agencyData?.text_color_secondary?.hex ?? "";

            return data;
          };

          const updatedColors = getColorsData();

          imageDesignData = {
            ...imageDesignData,
            base_color_primary: {
              r: 255,
              g: 0,
              b: 0,
              hex: updatedColors.base_color_primary?.hex,
            },
            base_color_secondary: {
              r: 255,
              g: 255,
              b: 255,
              hex: updatedColors?.base_color_secondary?.hex,
            },
            text_color_primary: {
              r: 255,
              g: 255,
              b: 255,
              hex: updatedColors?.text_color_primary?.hex,
            },
            text_color_secondary: {
              r: 255,
              g: 0,
              b: 0,
              hex: updatedColors?.text_color_secondary?.hex,
            },
          };

          let imageTemplateUrl = getDesignTemplateUrl(
            templateConfigData.image_post_design_svg_file.upload_record_id,
          );
          let storyTemplateUrl = getDesignTemplateUrl(
            templateConfigData.story_post_design_svg_file.upload_record_id,
          );
          let imageTemplateAssets =
            templateConfigData.image_post_design_svg_file;
          let storyTemplateAssets = templateConfigData.story_post_design_assets;

          imageDesignData.post_type = "image_post";

          let storyDesignData = CommonUtils.cloneDeep(imageDesignData);

          storyDesignData.post_type = "story_post";

          imageDesignData.template_url = {
            url: imageTemplateUrl,
            load: true,
          };
          storyDesignData.template_url = {
            url: storyTemplateUrl,
            load: true,
          };

          /**
           * Update property info in design data using the property response data
           */

          setCommonState((prevState) => ({
            ...prevState,
            loadingPostData: false,
            postData: updatedPost,
            propertyData: updatedPost?.property_id ?? null,
            agencyData: updatedPost?.agency_ref,
            agentData: updatedPost?.agent_ref,
            brandData: updatedPost?.agency_ref?.brand_id ?? null,
            designStage: updatedPost?.design_stage ?? "",
            templateConfigs:
              designTemplateConfigData[designStageFromParams?.trim() as any],
            loadingTemplateConfigs: false,
            useAgencyOfficeLogo: useAgencyOfficeLogo,
            imageTemplateAssets: imageTemplateAssets as any,
            storyTemplateAssets: storyTemplateAssets as any,
            imageDesignData: imageDesignData as any,
            storyDesignData: storyDesignData as any,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch property post:", err);
        if (isInitial) toast.error("Unable to load property post");
      } finally {
        if (isInitial) setLoading(false);
      }
    },
    [propertyPostId],
  );

  const fetchTimeline = useCallback(async () => {
    if (!propertyPostId) return;

    setTimelineLoading(true);
    try {
      const res = await PropertyApi.getPropertyPostTimeline(propertyPostId, {
        page: 1,
        limit: 50,
        created_at: "desc",
      });

      const docs = res?.data?.data?.docs || res?.data?.data?.data?.docs;
      setTimelineDocs(Array.isArray(docs) ? docs : []);
    } catch (e) {
      console.error(e);
      setTimelineDocs([]);
    } finally {
      setTimelineLoading(false);
    }
  }, [propertyPostId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  useEffect(() => {
    if (!propertyPostId) return;
    fetchPost(true);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [propertyPostId, fetchPost]);

  const issues = useMemo(() => {
    const post = propertyPost;
    if (!post) return [];

    const keys: PlatformKey[] = [
      "facebook",
      "instagram",
      "google_business",
      "linkedin",
      "tiktok",
      "youtube",
    ];

    return keys
      .map((k) => {
        const p = post?.[k];
        if (!p?.enabled) return null; // ← null return ho sakta hai

        const label = PLATFORM_LABEL[k];

        const issueRaw = p?.error || p?.warning;
        if (!issueRaw) return null; // ← null return ho sakta hai

        const parsed = normalizeMsg(issueRaw);
        const isError = !!p?.error;
        const variant: "error" | "warning" = isError ? "error" : "warning";

        const heading =
          variant === "error"
            ? `${label} post failed`
            : `${label}: Post update`;

        return {
          key: k,
          variant,
          heading,
          title: parsed?.title || undefined,
          message: parsed?.message || undefined,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null); // ← Type guard
  }, [propertyPost]);

  useEffect(() => {
    if (!scheduleModalOpen) return;

    const base = propertyPost?.schedule_datetime
      ? dayjs(propertyPost.schedule_datetime)
      : dayjs();

    setScheduleDate(base);
    setScheduleTime(base);

    setPerPlatformSchedule((prev) => ({
      ...prev,
      facebook: { date: base, time: base },
      instagram: { date: base, time: base },
      google_business: { date: base, time: base },
      linkedin: { date: base, time: base },
    }));
  }, [scheduleModalOpen, propertyPost]);

  // useEffect(() => {
  //   if (!propertyPost) return;

  //   setSelectedPlatforms((prev) => ({
  //     ...prev,
  //     Facebook: propertyPost?.facebook?.enabled ?? prev.Facebook,
  //     Instagram: propertyPost?.instagram?.enabled ?? prev.Instagram,
  //     google_business:
  //       propertyPost?.google_business?.enabled ?? prev["google_business"],
  //     LinkedIn: propertyPost?.linkedin?.enabled ?? prev["LinkedIn"],
  //   }));
  // }, [propertyPost]);

  useEffect(() => {
    if (!propertyPostId || !designStageFromParams) return;

    fetchPost(true);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [propertyPostId, fetchPost, designStageFromParams]);

  useEffect(() => {
    if (!commonState?.agencyData) return;

    fetchFontsData();
  }, [commonState.agencyData]);

  useEffect(() => {
    if (propertyPost?.schedule_datetime) {
      const scheduleDt = dayjs(propertyPost.schedule_datetime);
      const now = dayjs();

      if (scheduleDt.isBefore(now)) {
        setLocalPostStatus("published");
        setPublishedDateTime(scheduleDt);
        setScheduledDateTime(null);
        setShowScheduleForm(false);
      } else {
        setLocalPostStatus("scheduled");
        setScheduledDateTime(scheduleDt);
        setPublishedDateTime(null);
        setShowScheduleForm(false);
      }
    } else {
      setLocalPostStatus("draft");
      setPublishedDateTime(null);
      setScheduledDateTime(null);
    }
  }, [propertyPost]);

  const handleOpenEditSchedule = () => {
    const dt = propertyPost?.schedule_datetime
      ? dayjs(propertyPost.schedule_datetime)
      : dayjs();

    if (dt.isValid()) {
      setScheduleDate(dt);
      setScheduleTime(dt);
    }
    setEditScheduleModalOpen(true);
  };

  useEffect(() => {
    if (propertyPost) {
      const initialText =
        propertyPost?.post_text?.generated_caption || fullText;
      setPostCaption(initialText);
    }
  }, [propertyPost]);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const fullText = `Featuring: 5 🛌  Bedrooms | 5 🛀  Bathrooms | 2 🚗 Car Spaces... more
💸 Price Range: $1,675,000 - $1,842,000
Discover a lifestyle that's more than extraordinary – it's truly exceptional. Nestled in the heart of Berwick Waters, this stunning property at 6 Rockbank Road offers everything you need to live like a star.
Don't miss the chance to experience the allure of celebrity living. Come and see what makes 6 Rockbank Road the ultimate address for those who demand the best.
Your dream lifestyle awaits!
📸 Explore More: https://www.realestate.com.au/property-house-vic-clyde...
For more information or to book a viewing,

Learn more at BrandFeeds [Brand feeds Link for this property here]

Contact
Sukhdeep Singh 📲 0420 879 430
Mohan Singh 📲 0426 250 579
#celebrityliving #dreamhome #luxurylifestyle #clydenorthrealestate 
#berwickwaters #melbournerealestate #homesforsale #luxuryhomes 
#realestategoals #propertyforsale #dreamliving #familyhome 
#investmentproperty`;

  const shortText = `Experience Celebrity Living at 📍 6 Rockbank Road, Clyde North Spaces...`;

  const [postCaption, setPostCaption] = useState(fullText);

  const handleSaveCaption = async () => {
    setIsEditingCaption(false);

    setPropertyPost((prev: any) => ({
      ...prev,
      post_text: {
        ...prev?.post_text,
        generated_caption: postCaption,
        last_edited_at: new Date().toISOString(),
      },
    }));

    toast.success("Caption updated successfully");
  };

  // const handleCancelCaption = () => {
  //   setIsEditingCaption(false);
  //   if (propertyPost?.post_text) {
  //     setPostCaption(propertyPost.post_text);
  //   }
  // };

  const handleCancelCaption = () => {
    setIsEditingCaption(false);
    if (propertyPost?.post_text?.generated_caption) {
      setPostCaption(propertyPost.post_text.generated_caption);
    } else {
      setPostCaption(fullText);
    }
  };

  const getShortText = (text: string) => {
    if (!text) return "";
    const limit = 100;
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const resolvedPreviewImages = useMemo(() => {
    const list: string[] = [];
    const designRef = propertyPost?.image_post_design_activity_ref;

    if (designRef?.design_task_id?.output_image_path) {
      list.push(
        CommonHelpers.getImageUrlFromImageTool({
          path: designRef.design_task_id.output_image_path,
        }),
      );
    }
    if (designRef?.design_task_id?.child_design_tasks) {
      designRef.design_task_id.child_design_tasks.forEach((child: any) => {
        if (child.output_image_path) {
          list.push(
            CommonHelpers.getImageUrlFromImageTool({
              path: child.output_image_path,
            }),
          );
        }
      });
    }

    return list.length > 0
      ? list
      : [
          "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png",
          "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772880839/50e4b8a38cedf2a298e8727b499c7b1a0d3db07a_kuwymx.jpg",
          "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772881727/9f10bdd6592a13aee11ea1a151bc0ea6bdd6c3c2_2_p8qgju.jpg",
        ];
  }, [propertyPost]);

  const resolvedStoryImages = useMemo(() => {
    const list: string[] = [];
    const designRef = propertyPost?.story_post_design_activity_ref;

    if (designRef?.design_task_id?.output_image_path) {
      list.push(
        CommonHelpers.getImageUrlFromImageTool({
          path: designRef.design_task_id.output_image_path,
        }),
      );
    }
    if (designRef?.design_task_id?.child_design_tasks) {
      designRef.design_task_id.child_design_tasks.forEach((child: any) => {
        if (child.output_image_path) {
          list.push(
            CommonHelpers.getImageUrlFromImageTool({
              path: child.output_image_path,
            }),
          );
        }
      });
    }

    return list.length > 0
      ? list
      : [
          "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
        ];
  }, [propertyPost]);

  const handleOpenCustomize = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("step", "customize_post");
      return next;
    });
  };

  const formatPostType = (slug: string) => {
    if (!slug) return "";
    return slug
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderDesignImage = (item: any) => {
    const designActivityRef = item?._id ? item : null;

    const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
    const postType =
      designActivityRef?.design_post_type || item?.design_post_type;
    const designTaskStatus = designActivityRef?.design_task_id?.task_status;

    const isLoading = designTaskStatus === CommonEnums.taskStatus.queued;
    const isErrored = designTaskStatus === CommonEnums.taskStatus.errored;

    const assetUrl = assetUrlRaw
      ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
      : null;

    // --- Error State ---
    if (isErrored) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error Preview"
            className="activity-thumbnail placeholder-image"
          />
        </div>
      );
    }

    if (isLoading) {
      const fallback =
        postType === CommonEnums.postType.video_post.key
          ? "/images/square-fallback-image-for-video-preview.png"
          : "/images/square-fallback-image-for-image-preview.png";

      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src={fallback}
            alt="Loading Preview"
            className="activity-thumbnail placeholder-image"
          />
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    if (postType === CommonEnums.postType.story_post.key) {
      return (
        <div className="activity-image-section story-post-image-container">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Story Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.video_post.key) {
      return (
        <div className="activity-image-section video-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Video Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.reel_post.key) {
      return (
        <div className="activity-image-section reel-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Reel Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    return (
      <div className="activity-image-section">
        <img
          src={
            assetUrl ||
            "/images/image-video-generation-error-fallback-image-for-card.png"
          }
          alt="Image Preview"
          className="activity-thumbnail"
          onError={(e: any) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              "/images/image-video-generation-error-fallback-image-for-card.png";
          }}
        />
      </div>
    );
  };

  const getSelectedTemplateId = () => {
    if (!propertyPost) return "";

    switch (propertyPost.post_type) {
      case CommonEnums.postTypes.image_post:
        return (
          propertyPost?.image_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      case CommonEnums.postTypes.story_post:
        return (
          propertyPost?.story_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      case CommonEnums.postTypes.video_post:
        return (
          propertyPost?.video_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      case CommonEnums.postTypes.reel_post:
        return (
          propertyPost?.reel_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      default:
        return propertyPost?.template_id || "";
    }
  };

  const designStatuses = [
    propertyPost?.image_post_design_activity_ref?.design_status,
    propertyPost?.story_post_design_activity_ref?.design_status,
    propertyPost?.video_post_design_activity_ref?.design_status,
    propertyPost?.reel_post_design_activity_ref?.design_status,
  ].filter(Boolean);

  const hasPendingForUpload = designStatuses.includes(
    "pending_for_social_media_upload",
  );

  const formattedSchedule = propertyPost?.schedule_datetime
    ? dayjs(propertyPost?.schedule_datetime).format(
        "MMMM DD, YYYY [at] hh:mm A",
      )
    : "-";

  // const handlePostNow = async (
  //   propertyPostId: string,
  //   designActivityRef: any,
  //   caption: string,
  // ) => {
  //   if (!propertyPostId || !designActivityRef) {
  //     toast.error("Property post or design not selected.");
  //     return;
  //   }

  //   setUpdating(true);

  //   try {
  //     const currentDateTime = dayjs().toISOString();

  //     const res = await PropertyApi.updatePropertyPost(propertyPostId, {
  //       schedule_type: "post_now",
  //       schedule_datetime: currentDateTime,
  //     });

  //     if (res?.data?.success) {
  //       toast.success("Post updated in database successfully!");
  //       setPropertyPost((prev: any) => ({ ...prev, ...res.data.data }));
  //       await fetchTimeline();
  //     } else {
  //       toast.error(res?.data?.message || "Failed to update property post");
  //       return;
  //     }

  //     const designTaskStatus = designActivityRef?.design_task_id?.task_status;
  //     const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;

  //     if (designTaskStatus === CommonEnums.taskStatus.errored) {
  //       toast.error("Design task errored. Cannot post this item.");
  //       return;
  //     }

  //     if (!assetUrlRaw) {
  //       toast.error("No processed image/video found to post.");
  //       return;
  //     }

  //     const assetUrl = CommonHelpers.getImageUrlFromImageTool({
  //       path: assetUrlRaw,
  //     });

  //     try {
  //       await SocialMediaApi.postFacebookImage({
  //         imageUrl: assetUrl,
  //         caption,
  //         scheduleType: "post_now",
  //       });
  //       toast.success("Post published to Facebook successfully!");
  //     } catch (fbError: any) {
  //       console.error("Error posting to Facebook:", fbError);
  //       toast.error(
  //         fbError?.response?.data?.message ||
  //           "Post updated in DB but failed to publish on Facebook",
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Error publishing now:", error);
  //     toast.error("Something went wrong while publishing");
  //   } finally {
  //     setUpdating(false);
  //   }
  // };

  const openPlatformPostNow = () => {
    if (!activePlatformKey) {
      toast.error("This platform is not supported yet");
      return;
    }
    if (!selectedPlatforms[activeTab]) {
      toast.error(`${activePlatformLabel} is not selected`);
      return;
    }
    setPlatformPostNowOpen(true);
  };

  const openScheduleModal = () => {
    const publishingSchedule = userPreferences?.publishing_schedule;

    console.log("🟢 Schedule modal — publishingSchedule:", publishingSchedule);

    if (publishingSchedule === "different_for_each_platform") {
      setSameScheduleAll(false);
    } else {
      setSameScheduleAll(true);
    }

    setScheduleModalOpen(true);
  };

  const openPlatformSchedule = () => {
    if (!activePlatformKey) {
      toast.error("This platform is not supported yet");
      return;
    }
    if (!selectedPlatforms[activeTab]) {
      toast.error(`${activePlatformLabel} is not selected`);
      return;
    }

    const dtRaw = propertyPost?.[activePlatformKey]?.schedule_datetime;
    const dt = dtRaw ? dayjs(dtRaw) : dayjs();

    setPlatformScheduleDate(dt);
    setPlatformScheduleTime(dt);
    setPlatformScheduleOpen(true);
  };

  const handlePlatformPostNow = async () => {
    if (!propertyPostId || updating) return;
    if (!activePlatformKey) return;

    setPlatformPostNowOpen(false);
    setUpdating(true);
    setSavingAction("post_now");
    setSavingModalOpen(true);

    try {
      const formData = await buildPostPayload({
        platform: activePlatformKey,
        schedule_type: "post_now",
      });

      const res = await PropertyApi.updatePropertyPostMultipart(
        propertyPostId,
        formData,
      );

      setSavingModalOpen(false);
      setSavingAction(null);

      if (res?.data?.success) {
        toast.success(`Posting to ${activePlatformLabel} started`);
        await fetchPost(true);
        await fetchTimeline();
      } else {
        toast.error(res?.data?.message || "Failed to publish now");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
      setSavingModalOpen(false);
      setSavingAction(null);
    } finally {
      setUpdating(false);
    }
  };

  const handlePlatformSchedule = async () => {
    if (!propertyPostId || updating) return;
    if (!activePlatformKey) return;

    if (!platformScheduleDate || !platformScheduleTime) {
      toast.error("Please select both date and time");
      return;
    }

    const combined = platformScheduleDate
      .hour(platformScheduleTime.hour())
      .minute(platformScheduleTime.minute())
      .second(0)
      .millisecond(0);

    if (!combined.isValid()) {
      toast.error("Invalid date/time");
      return;
    }

    setPlatformScheduleOpen(false);
    setUpdating(true);
    setSavingAction("schedule");
    setSavingModalOpen(true);

    try {
      const formData = await buildPostPayload({
        platform: activePlatformKey,
        schedule_type: "schedule_datetime",
        schedule_datetime: combined.toISOString(),
      });

      const res = await PropertyApi.updatePropertyPostMultipart(
        propertyPostId,
        formData,
      );

      setSavingModalOpen(false);
      setSavingAction(null);

      if (res?.data?.success) {
        toast.success(`${activePlatformLabel} scheduled`);
        await fetchPost(true);
        await fetchTimeline();
      } else {
        toast.error(res?.data?.message || "Failed to schedule");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
      setSavingModalOpen(false);
      setSavingAction(null);
    } finally {
      setUpdating(false);
    }
  };

  const handlePlatformCancelSchedule = async () => {
    if (!propertyPostId || updating) return;
    if (!activePlatformKey) return;

    setUpdating(true);
    try {
      const payload = {
        platform: activePlatformKey,
        schedule_type: null,
        schedule_datetime: null,
      };

      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);

      if (res?.data?.success) {
        toast.success(`${activePlatformLabel} schedule cancelled`);
        await fetchPost(true);
        await fetchTimeline();
      } else {
        toast.error(res?.data?.message || "Failed to cancel schedule");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  const handlePostNow = async () => {
    if (!propertyPostId || updating) return;

    const selected = getSelectedSupportedPlatforms();
    if (selected.length === 0) {
      toast.error("Please select at least one platform.");
      return;
    }

    setPostNowModalOpen(false);
    setUpdating(true);
    setSavingAction("post_now");
    setSavingModalOpen(true);

    try {
      const nowIso = dayjs().toISOString();

      const formData = await buildPostPayload({
        schedule_type: "post_now",
        schedule_datetime: nowIso,
      });

      const res = await PropertyApi.updatePropertyPostMultipart(
        propertyPostId,
        formData,
      );

      setSavingModalOpen(false);
      setSavingAction(null);

      if (res?.data?.success) {
        toast.success("Posting started for selected platforms!");
        await fetchPost(true);
        await fetchTimeline();
      } else {
        toast.error(res?.data?.message || "Failed to publish now");
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Something went wrong while posting now");
      setSavingModalOpen(false);
      setSavingAction(null);
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmCancelSchedule = async () => {
    if (!propertyPostId || updating) return;

    setUpdating(true);
    try {
      const res = await PropertyApi.updatePropertyPost(propertyPostId, {
        schedule_type: null,
        schedule_datetime: null,
      });

      if (res?.data?.success) {
        toast.success("Schedule cancelled");
        setPropertyPost((prev: any) => ({
          ...prev,
          schedule_type: null,
          schedule_datetime: null,
        }));
        setCancelScheduleModalOpen(false);
        await fetchTimeline();
      } else {
        toast.error(res?.data?.message || "Failed to cancel schedule");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  type PublishingSchedule =
    | "same_for_all_platforms"
    | "different_for_each_platform";

  const getPublishingScheduleValue = (): PublishingSchedule =>
    sameScheduleAll ? "same_for_all_platforms" : "different_for_each_platform";

  const handleConfirmSchedule = async () => {
    if (!propertyPostId || updating) return;

    const selected = getSelectedSupportedPlatforms();
    if (selected.length === 0) {
      toast.error("Please select at least one platform to schedule.");
      return;
    }

    if (sameScheduleAll) {
      if (!scheduleDate || !scheduleTime) {
        toast.error("Please select both date and time to schedule.");
        return;
      }
      const combinedDateTime = combineDayjsDateTime(scheduleDate, scheduleTime);
      if (!combinedDateTime) {
        toast.error("Invalid date/time selected.");
        return;
      }
    }

    setScheduleModalOpen(false);
    setEditScheduleModalOpen(false);
    setUpdating(true);
    setSavingAction("schedule");
    setSavingModalOpen(true);

    try {
      if (sameScheduleAll) {
        const combinedDateTime = combineDayjsDateTime(
          scheduleDate,
          scheduleTime,
        )!;

        const formData = await buildPostPayload({
          schedule_type: "schedule_datetime",
          schedule_datetime: combinedDateTime.toISOString(),
          publishing_schedule: "same_for_all_platforms",
        });

        const res = await PropertyApi.updatePropertyPostMultipart(
          propertyPostId,
          formData,
        );

        setSavingModalOpen(false);
        setSavingAction(null);

        if (res?.data?.success) {
          toast.success("Post scheduled successfully!");
          await fetchPost(true);
          await fetchTimeline();
        } else {
          toast.error(res?.data?.message || "Failed to schedule post");
        }
        return;
      }

      const allPlatforms: SchedulePlatformKey[] = [
        "facebook",
        "instagram",
        "google_business",
        "linkedin",
      ];

      const firstPlatform = allPlatforms[0];
      const firstSchedule = perPlatformSchedule[firstPlatform];
      const firstDt = combineDayjsDateTime(
        firstSchedule?.date || null,
        firstSchedule?.time || null,
      );

      if (!firstDt) {
        toast.error(`Invalid date/time for ${firstPlatform}`);
        setSavingModalOpen(false);
        setSavingAction(null);
        return;
      }

      const baseFormData = await buildPostPayload({
        schedule_type: "schedule_datetime",
        schedule_datetime: firstDt.toISOString(),
        publishing_schedule: "different_for_each_platform",
      });

      const baseRes = await PropertyApi.updatePropertyPostMultipart(
        propertyPostId,
        baseFormData,
      );

      if (!baseRes?.data?.success) {
        setSavingModalOpen(false);
        setSavingAction(null);
        toast.error("Failed to initialize schedule");
        return;
      }

      const remainingPlatforms = allPlatforms.slice(1);

      const calls = remainingPlatforms.map(async (platform) => {
        const v = perPlatformSchedule[platform];
        const dt = combineDayjsDateTime(v?.date || null, v?.time || null);

        if (!dt)
          throw new Error(`Please select valid date/time for ${platform}.`);

        const formData = new FormData();
        formData.append("platform", platform);
        formData.append("schedule_type", "schedule_datetime");
        formData.append("schedule_datetime", dt.toISOString());
        formData.append("publishing_schedule", "different_for_each_platform");

        return PropertyApi.updatePropertyPostMultipart(
          propertyPostId,
          formData,
        );
      });

      const results = await Promise.allSettled(calls);

      setSavingModalOpen(false);
      setSavingAction(null);

      const anyFailed = results.some(
        (r) =>
          r.status === "rejected" ||
          (r.status === "fulfilled" && !r.value?.data?.success),
      );

      if (anyFailed) {
        toast.error("Some platforms failed to schedule. Please try again.");
      } else {
        toast.success("Scheduled successfully for all platforms!");
      }

      await fetchPost(true);
      await fetchTimeline();
    } catch (error: any) {
      console.error("Error scheduling post:", error);
      toast.error(error?.message || "Something went wrong while scheduling");
      setSavingModalOpen(false);
      setSavingAction(null);
    } finally {
      setUpdating(false);
    }
  };

  const handleShowForm = (mode: "schedule" | "auto") => {
    setScheduleMode(mode);
    setTimeout(() => {
      scheduleFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  // const previewImages = [
  //   "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png",
  //   "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772880839/50e4b8a38cedf2a298e8727b499c7b1a0d3db07a_kuwymx.jpg",
  //   "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772881727/9f10bdd6592a13aee11ea1a151bc0ea6bdd6c3c2_2_p8qgju.jpg",
  // ];

  const storyImages = [
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
  ];

  useEffect(() => {
    if (!storyImages.length) return;

    // const timer = setTimeout(() => {
    //   setPostStoryPreviewIndex((prev) =>
    //     prev === storyImages.length - 1 ? 0 : prev + 1,
    //   );
    // }, 7000);

    // return () => clearTimeout(timer);
  }, [postStoryPreviewIndex]);

  useEffect(() => {
    if (!scheduleDate && !scheduleTime) {
      const now = dayjs();
      setScheduleDate(now);
      setScheduleTime(now);
    }
  }, []);

  const nextSlide = () => {
    setPostPreviewIndex((prev) =>
      prev === previewImages.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setPostPreviewIndex((prev) =>
      prev === 0 ? previewImages.length - 1 : prev - 1,
    );
  };

  const storyNextSlide = () => {
    setPostStoryPreviewIndex((prev) =>
      prev === storyImages.length - 1 ? 0 : prev + 1,
    );
  };

  const storyPrevSlide = () => {
    setPostStoryPreviewIndex((prev) =>
      prev === 0 ? storyImages.length - 1 : prev - 1,
    );
  };

  const isPublished = (t: any) => t?.status === "completed";
  const isPending = (t: any) => t?.status === "pending";

  const orderedTimelineDocs = useMemo(() => {
    const items = (timelineDocs || []).filter((t) => t?.status !== "cancelled");

    const scheduledPending = items
      .filter(
        (t) =>
          t?.schedule_type === "schedule_datetime" && t?.status === "pending",
      )
      .sort(
        (a, b) =>
          dayjs(a?.schedule_datetime).valueOf() -
          dayjs(b?.schedule_datetime).valueOf(),
      );

    const postNow = items
      .filter((t) => t?.schedule_type === "post_now")
      .sort(
        (a, b) =>
          dayjs(b?.schedule_datetime).valueOf() -
          dayjs(a?.schedule_datetime).valueOf(),
      );

    const others = items.filter(
      (t) =>
        !(
          (t?.schedule_type === "schedule_datetime" &&
            t?.status === "pending") ||
          t?.schedule_type === "post_now"
        ),
    );

    return [...scheduledPending, ...postNow, ...others];
  }, [timelineDocs]);

  const CustomCalendarIcon = () => {
    return (
      <img
        src="/images/Calender.svg"
        alt="calendar"
        style={{
          width: "18px",
          height: "20px",
          opacity: 1,
        }}
      />
    );
  };

  const CustomClockIcon = () => {
    return (
      <img
        src="/images/clockicon.svg"
        alt="clock"
        style={{ width: "18px", height: "18px", opacity: 1 }}
      />
    );
  };

  const formatTimelineDateTime = (dt: string) =>
    dayjs(dt).format("MMM D, YYYY hh:mm A");

  const platformData = activePlatformKey
    ? propertyPost?.[activePlatformKey]
    : null;

  const getStatusConfig = (pData: any) => {
    if (!pData || !pData.enabled) {
      return { label: "Pending", variant: "pending" };
    }

    const status = pData.status;

    // 1. Issues detected
    if (status === "issues_detected" || status === "failed" || pData.error) {
      return { label: "Issues detected", variant: "error" };
    }

    if (status === "completed") {
      return { label: "Completed", variant: "completed" };
    }

    // 3. Scheduled
    if (
      status === "scheduled" ||
      (pData.schedule_type === "schedule_datetime" &&
        pData.schedule_datetime &&
        dayjs(pData.schedule_datetime).isAfter(dayjs()))
    ) {
      return { label: "Scheduled", variant: "scheduled" };
    }

    // 4. Default → Pending
    return { label: "Pending", variant: "pending" };
  };

  const statusConfig = getStatusConfig(platformData);

  const scheduledOrPublishedDt =
    platformData?.schedule_datetime || platformData?.uploaded_at || null;

  const showScheduledFor = true;
  const showPostLink = !!platformData?.permalink_url;

  const platformStatus =
    platformData?.status || CommonEnums.socialUploadStatus.not_started;
  const platformPermalink = platformData?.permalink_url || "-";

  const platformScheduledFor = platformData?.schedule_datetime
    ? dayjs(platformData.schedule_datetime).format("MMM D, YYYY • hh:mm A")
    : "-";

  const captionBlocks = [
    {
      key: "positioning",
      className: "block-positioning",
    },
    {
      key: "specs",
      className: "block-specs",
    },
    {
      key: "price_block",
      className: "block-price",
    },
    {
      key: "feature_summary",
      className: "block-features",
    },
    {
      key: "location_lifestyle",
      className: "block-location",
    },
    {
      key: "inspection_info",
      className: "block-inspection",
    },
    {
      key: "cta",
      className: "block-cta",
    },
    {
      key: "compliance",
      className: "block-compliance",
    },
  ];

  const fetchConnectedPlatforms = useCallback(async () => {
    try {
      const res = await SocialMediaApi.getAllConnectedAccounts();
      const accounts = res?.data?.accounts || [];

      const isConnected = (platform: string) => {
        return accounts.some(
          (acc: any) => acc.platform === platform && acc.status === "active",
        );
      };

      setConnectedPlatforms({
        Facebook: isConnected("facebook"),
        Instagram: isConnected("instagram"),
        google_business: isConnected("google_business"),
        LinkedIn: isConnected("linkedin"),
      });
    } catch (e) {
      console.error("Error fetching connected platforms:", e);
      setConnectedPlatforms({
        Facebook: false,
        Instagram: false,
        google_business: false,
        LinkedIn: false,
      });
    }
  }, []);

  useEffect(() => {
    fetchConnectedPlatforms();
  }, [fetchConnectedPlatforms]);

  const applyPreferencesToUi = (prefs: any) => {
    if (!prefs) {
      setSelectedPlatforms({
        Facebook: true,
        Instagram: true,
        google_business: true,
        LinkedIn: true,
        TikTok: false,
        YouTube: false,
      });
      setSameCaptionForAll(false);
      setAlwaysShareStory(false);
      return;
    }

    setSelectedPlatforms({
      Facebook: prefs?.facebook?.enabled !== false,
      Instagram: prefs?.instagram?.enabled !== false,
      google_business: prefs?.google_business?.enabled !== false,
      LinkedIn: prefs?.linkedin?.enabled !== false,
      TikTok: false,
      YouTube: false,
    });

    if (prefs?.publishing_schedule === "different_for_each_platform") {
      setSameScheduleAll(false);
    } else {
      setSameScheduleAll(true);
    }

    setSameCaptionForAll(prefs?.same_caption_for_all_platforms ?? false);
    setAlwaysShareStory(prefs?.always_share_story ?? false);
  };

  const fetchPostPreferences = useCallback(async () => {
    const postType = "property_posts";

    try {
      const res = await PostPreferencesApi.getPostPreferences(postType);

      if (
        res?.data?.success &&
        res.data.data &&
        res.data.data.post_type === "property_posts"
      ) {
        setUserPreferences(res.data.data);
        applyPreferencesToUi(res.data.data);
      } else {
        setUserPreferences(null);
        setSelectedPlatforms({
          Facebook: true,
          Instagram: true,
          google_business: true,
          LinkedIn: true,
          TikTok: false,
          YouTube: false,
        });
      }
    } catch (e) {
      console.error("Error fetching preferences:", e);
      setUserPreferences(null);
      setSelectedPlatforms({
        Facebook: true,
        Instagram: true,
        google_business: true,
        LinkedIn: true,
        TikTok: false,
        YouTube: false,
      });
    }
  }, []);

  useEffect(() => {
    fetchPostPreferences();
  }, [fetchPostPreferences]);

  const handleSaveChanges = async () => {
    if (updating) return;

    setUpdating(true);
    setSavingAction("save_changes");
    setSavingModalOpen(true);

    try {
      if (savePostPreferences) {
        const postType = "property_posts";

        const payload = {
          post_type: postType,
          facebook: { enabled: !!selectedPlatforms.Facebook },
          instagram: { enabled: !!selectedPlatforms.Instagram },
          google_business: { enabled: !!selectedPlatforms.google_business },
          linkedin: { enabled: !!selectedPlatforms.LinkedIn },
          publishing_schedule: sameScheduleAll
            ? "same_for_all_platforms"
            : "different_for_each_platform",
          same_caption_for_all_platforms: sameCaptionForAll,
          always_share_story: alwaysShareStory,
        };

        const res = await PostPreferencesApi.savePostPreferences(payload);

        if (!res?.data?.success) {
          setSavingModalOpen(false);
          setSavingAction(null);
          toast.error(res?.data?.message || "Failed to save preferences");
          return;
        }
      }

      if (!propertyPostId) {
        setSavingModalOpen(false);
        setSavingAction(null);
        toast.error("Property post id missing");
        return;
      }

      const imageBlob = await getCanvasBlobFromRef(
        imagePreviewRef,
        "image/jpeg",
        1,
      );
      const storyBlob = await getCanvasBlobFromRef(
        storyPreviewRef,
        "image/jpeg",
        1,
      );

      if (!imageBlob && !storyBlob) {
        setSavingModalOpen(false);
        setSavingAction(null);
        toast.error("Preview not ready yet");
        return;
      }

      const formData = new FormData();

      if (imageBlob) {
        formData.append(
          "image_post_design",
          imageBlob,
          `image-post-${propertyPostId}.jpg`,
        );
      }

      if (storyBlob) {
        formData.append(
          "story_post_design",
          storyBlob,
          `story-post-${propertyPostId}.jpg`,
        );
      }

      const updateRes = await PropertyApi.updatePropertyPostMultipart(
        propertyPostId,
        formData,
      );

      setSavingModalOpen(false);
      setSavingAction(null);

      if (updateRes?.data?.success) {
        toast.success("Saved");
        await fetchPost(true);
        await fetchTimeline();
      } else {
        toast.error(updateRes?.data?.message || "Failed to update design");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
      setSavingModalOpen(false);
      setSavingAction(null);
    } finally {
      setUpdating(false);
    }
  };

  const getPropertyImageUrl = (img: any) => {
    if (!img)
      return "/images/image-video-generation-error-fallback-image-for-card.png";

    // Use medium first (faster), then fallback
    if (img?.medium_image_url) return getCdnImageUrl(img.medium_image_url);
    if (img?.large_image_url) return getCdnImageUrl(img.large_image_url);
    if (img?.extra_large_image_url)
      return getCdnImageUrl(img.extra_large_image_url);
    if (img?.original_image_url) return getCdnImageUrl(img.original_image_url);
    if (img?.storage_key) return getCdnImageUrl(img.storage_key);

    return "/images/image-video-generation-error-fallback-image-for-card.png";
  };

  const getLargePropertyImageUrl = (img: any) => {
    if (!img)
      return "/images/image-video-generation-error-fallback-image-for-card.png";

    if (img?.large_image_url) return getCdnImageUrl(img.large_image_url);
    if (img?.large_image_url) return getCdnImageUrl(img.large_image_url);
    if (img?.original_image_url) return getCdnImageUrl(img.original_image_url);
    if (img?.storage_key) return getCdnImageUrl(img.storage_key);
    if (img?.medium_image_url) return getCdnImageUrl(img.medium_image_url);

    return "/images/image-video-generation-error-fallback-image-for-card.png";
  };

  const filteredPropertyImages = useMemo(() => {
    const allImages = propertyPost?.property_id?.property_images || [];
    const thumbnailId = propertyPost?.property_id?.thumbnail_image_ref?._id;

    if (!thumbnailId) return allImages;

    return allImages.filter((img: any) => img._id !== thumbnailId);
  }, [propertyPost]);

  useEffect(() => {
    if (!filteredPropertyImages.length) return;

    filteredPropertyImages.forEach((img: any) => {
      const url = getPropertyImageUrl(img);
      if (!url) return;

      const image = new Image();
      image.src = url;
    });
  }, [filteredPropertyImages]);

  const totalSlides = 1 + filteredPropertyImages.length;

  const nextPropertyImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPropertyImageIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };

  const prevPropertyImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPropertyImageIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const getCanvasImageFromRef = (
    wrapperRef: React.RefObject<HTMLDivElement>,
  ) => {
    const root = wrapperRef?.current;
    if (!root) return null;

    const canvas =
      root.querySelector("canvas.lower-canvas") || root.querySelector("canvas");

    if (!canvas) return null;

    try {
      return (canvas as HTMLCanvasElement).toDataURL("image/png");
    } catch (e) {
      console.error("Canvas export failed:", e);
      return null;
    }
  };

  // Naya helper: Canvas se Blob nikalo
  const getCanvasBlobFromRef = (
    wrapperRef: React.RefObject<HTMLDivElement>,
    format: "image/jpeg" | "image/png" = "image/jpeg",
    quality = 1,
  ): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const root = wrapperRef?.current;
      if (!root) return resolve(null);

      const canvas =
        root.querySelector("canvas.lower-canvas") ||
        root.querySelector("canvas");

      if (!canvas) return resolve(null);

      try {
        (canvas as HTMLCanvasElement).toBlob(
          (blob) => resolve(blob),
          format,
          quality,
        );
      } catch (e) {
        console.error("Canvas export failed:", e);
        resolve(null);
      }
    });
  };

  const buildPostPayload = async ({
    platform,
    schedule_type,
    schedule_datetime,
    publishing_schedule,
  }: {
    platform?: string;
    schedule_type?: "post_now" | "schedule_datetime" | null;
    schedule_datetime?: string | null;
    publishing_schedule?: string;
  }) => {
    const formData = new FormData();

    // 1. Text fields
    if (platform) formData.append("platform", platform);
    if (schedule_type) formData.append("schedule_type", schedule_type);
    if (schedule_datetime)
      formData.append("schedule_datetime", schedule_datetime);
    if (publishing_schedule)
      formData.append("publishing_schedule", publishing_schedule);

    // 2. Image post design — canvas se blob nikalo
    const imageBlob = await getCanvasBlobFromRef(
      imagePreviewRef,
      "image/jpeg",
      1,
    );
    if (imageBlob) {
      formData.append(
        "image_post_design",
        imageBlob,
        `image-post-${propertyPostId}.jpg`,
      );
    }

    // 3. Story post design — canvas se blob nikalo
    const storyBlob = await getCanvasBlobFromRef(
      storyPreviewRef,
      "image/jpeg",
      1,
    );
    if (storyBlob) {
      formData.append(
        "story_post_design",
        storyBlob,
        `story-post-${propertyPostId}.jpg`,
      );
    }

    return formData;
  };

  const openImagePreviewModal = (clickedIndex?: number) => {
    const modalImages: { medium: string; large: string }[] = [];

    const canvasDataUrl = getCanvasImageFromRef(imagePreviewRef);
    if (canvasDataUrl) {
      modalImages.push({ medium: canvasDataUrl, large: canvasDataUrl });
    }

    filteredPropertyImages.forEach((img: any) => {
      const mediumUrl = getPropertyImageUrl(img);
      const largeUrl = getLargePropertyImageUrl(img);
      if (largeUrl) {
        modalImages.push({ medium: mediumUrl, large: largeUrl });
      }
    });

    if (modalImages.length === 0) {
      toast.error("Preview not ready yet");
      return;
    }

    const startIndex =
      typeof clickedIndex === "number"
        ? Math.min(clickedIndex, modalImages.length - 1)
        : Math.min(propertyImageIndex, modalImages.length - 1);

    setPreviewImagesForModal(modalImages);
    setImagePreviewStartIndex(startIndex);
    setIsImagePreviewOpen(true);
  };

  const downloadCanvasImage = (
    wrapperRef: React.RefObject<HTMLDivElement>,
    fileName: string,
  ) => {
    const dataUrl = getCanvasImageFromRef(wrapperRef);
    if (!dataUrl) {
      toast.error("Download not ready yet");
      return;
    }

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePlatformNotConnectedClick = (platformName: string) => {
    setNotConnectedPlatform(platformName);
    setNotConnectedModalOpen(true);
  };

  const getDisconnectedPlatforms = (): string[] => {
    const disconnected: string[] = [];

    if (selectedPlatforms["Facebook"] && !connectedPlatforms["Facebook"]) {
      disconnected.push("Facebook");
    }
    if (selectedPlatforms["Instagram"] && !connectedPlatforms["Instagram"]) {
      disconnected.push("Instagram");
    }
    if (
      selectedPlatforms["google_business"] &&
      !connectedPlatforms["google_business"]
    ) {
      disconnected.push("Google Business");
    }
    if (selectedPlatforms["LinkedIn"] && !connectedPlatforms["LinkedIn"]) {
      disconnected.push("LinkedIn");
    }

    return disconnected;
  };

  const handlePostNowClick = () => {
    const disconnected = getDisconnectedPlatforms();

    if (disconnected.length > 0) {
      setNotConnectedPlatform(disconnected[0]);
      setNotConnectedModalOpen(true);
      return;
    }

    setPostNowModalOpen(true);
  };

  const handleSchedulePostClick = () => {
    const disconnected = getDisconnectedPlatforms();

    if (disconnected.length > 0) {
      setNotConnectedPlatform(disconnected[0]);
      setNotConnectedModalOpen(true);
      return;
    }

    openScheduleModal();
  };

  if (showDesignPendingModal) {
    return (
      <DesignPendingModal
        open={true}
        onClose={() => setShowDesignPendingModal(false)}
      />
    );
  }

  if (commonState.loadingPostData && !propertyPost) {
    return <p>Loading post data...</p>;
  }

  if (commonState.downloadingFontsData && !propertyPost) {
    return <p>Initializing agency data...</p>;
  }

  if (commonState.loadingFontsData && !propertyPost) {
    return <p>Loading agency data...</p>;
  }

  return (
    <div className="setup-create-property-post">
      <div className="post-preview-wrapper-container">
        <PostHeader
          propertyPostId={propertyPostId}
          propertyPost={propertyPost}
          updating={updating}
          getStageLabel={getStageLabel}
          CommonHelpers={CommonHelpers}
          onPostNow={() => setPostNowModalOpen(true)}
          onSchedulePost={openScheduleModal}
          onUpdated={async () => {
            await fetchPost(true);
            await fetchTimeline();
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedPlatforms={selectedPlatforms}
          togglePlatformSelected={togglePlatformSelected}
          activePlatformKey={activePlatformKey}
          activePlatformLabel={activePlatformLabel}
          statusConfig={statusConfig}
          platformScheduledFor={platformScheduledFor}
          openPlatformPostNow={openPlatformPostNow}
          openPlatformSchedule={openPlatformSchedule}
          handlePlatformCancelSchedule={handlePlatformCancelSchedule}
          platformData={platformData}
          connectedPlatforms={connectedPlatforms}
          onPlatformNotConnectedClick={handlePlatformNotConnectedClick}
        />

        <SocialMediaNotConnectedModal
          open={notConnectedModalOpen}
          onClose={() => setNotConnectedModalOpen(false)}
          platformName={notConnectedPlatform}
        />

        {/* <div className="configure-post">
          <h3 className="section-title">Configure Post</h3>
          <div className="right-actions">
            <div className="right-actions">
              <button
                className="preview"
                disabled={!hasPendingForUpload}
                onClick={() => setIsPreviewOpen(true)}
              >
                Preview
              </button>
              <button
                className="change-template"
                disabled={!hasPendingForUpload}
                onClick={() => setIsUpdateTemplateOpen(true)}
              >
                Change Template
              </button>
            </div>
          </div>
        </div> */}

        <div className="post-preview-wrapper">
          {/* {hasPendingForUpload ? (
            <div className="post-preview-card">
              <div className="post-header">
                <img
                  src={
                    getImageUrl(
                      propertyPost?.agent_ref?.profile_image_ref
                        ?.compressed_file_path || "",
                    ) || "/images/profile-placeholder-image.png"
                  }
                  alt="Agent"
                  className="avatar"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src =
                      "/images/profile-placeholder-image.png";
                  }}
                />
                <div className="user-info">
                  <strong className="name">
                    {propertyPost.agent_ref?.principal_agent_first_name}{" "}
                    {propertyPost.agent_ref?.principal_agent_last_name}
                  </strong>
                  <span className="date">{formattedSchedule}</span>
                </div>
              </div>

              <div className="post-text">
                <span className="post-content">
                  {isExpanded ? fullText : shortText}
                </span>
                <span className="see-more" onClick={toggleExpand}>
                  {isExpanded ? " See less" : " See more"}
                </span>
              </div>

              <div className="carousel-container">
                {renderDesignImage(
                  [
                    propertyPost?.image_post_design_activity_ref,
                    propertyPost?.story_post_design_activity_ref,
                    propertyPost?.video_post_design_activity_ref,
                    propertyPost?.reel_post_design_activity_ref,
                  ].filter(Boolean)[postPreviewIndex],
                )}

                {[
                  propertyPost?.image_post_design_activity_ref,
                  propertyPost?.story_post_design_activity_ref,
                  propertyPost?.video_post_design_activity_ref,
                  propertyPost?.reel_post_design_activity_ref,
                ].filter(Boolean).length > 1 && (
                  <>
                    <button
                      className="carousel-button left"
                      onClick={() =>
                        setPostPreviewIndex((prev) =>
                          prev === 0
                            ? [
                                propertyPost?.image_post_design_activity_ref,
                                propertyPost?.story_post_design_activity_ref,
                                propertyPost?.video_post_design_activity_ref,
                                propertyPost?.reel_post_design_activity_ref,
                              ].filter(Boolean).length - 1
                            : prev - 1,
                        )
                      }
                    >
                      <LeftArrowIcon />
                    </button>

                    <button
                      className="carousel-button right"
                      onClick={() =>
                        setPostPreviewIndex((prev) =>
                          prev ===
                          [
                            propertyPost?.image_post_design_activity_ref,
                            propertyPost?.story_post_design_activity_ref,
                            propertyPost?.video_post_design_activity_ref,
                            propertyPost?.reel_post_design_activity_ref,
                          ].filter(Boolean).length -
                            1
                            ? 0
                            : prev + 1,
                        )
                      }
                    >
                      <RightArrowIcon />
                    </button>
                  </>
                )}
              </div>

              {[
                propertyPost?.image_post_design_activity_ref,
                propertyPost?.story_post_design_activity_ref,
                propertyPost?.video_post_design_activity_ref,
                propertyPost?.reel_post_design_activity_ref,
              ].filter(Boolean).length > 1 && (
                <div className="dots-pagination">
                  {[
                    propertyPost?.image_post_design_activity_ref,
                    propertyPost?.story_post_design_activity_ref,
                    propertyPost?.video_post_design_activity_ref,
                    propertyPost?.reel_post_design_activity_ref,
                  ]
                    .filter(Boolean)
                    .map((_, i) => (
                      <span
                        key={i}
                        className={`dot ${
                          i === postPreviewIndex ? "active" : ""
                        }`}
                        onClick={() => setPostPreviewIndex(i)}
                      />
                    ))}
                </div>
              )}
            </div>
          ) : (
            <div className="post-preview-loading">
              <div className="spinner" />
              <h4>Your request is being processed.</h4>
              <p>
                We’re working on your request! Creating a high-quality output
                can take a little while. We appreciate your patience.
              </p>
              <p className="message">
                Feel free to wait here, or browse other pages. We’ll send you a
                notification when your request is completed.
              </p>
            </div>
          )} */}
          <div className="previews-container">
            <div className="preview-column">
              <h3>Image post preview</h3>
              <div className="post-preview-card">
                <div className="post-header">
                  <img
                    // src={
                    //   getImageUrl(
                    //     propertyPost?.agent_ref?.profile_image_ref
                    //       ?.compressed_file_path || "",
                    //   ) || "/images/profile-placeholder-image.png"
                    // }
                    src={
                      getAgentProfileImageUrl(
                        propertyPost?.agent_ref?.profile_image_ref,
                      ) ||
                      getImageUrl(
                        propertyPost?.agent_ref?.profile_image_ref
                          ?.compressed_file_path || "",
                      ) ||
                      "/images/profile-placeholder-image.png"
                    }
                    alt="Agent"
                    className="avatar"
                  />
                  <div className="user-info">
                    <span className="name">
                      {propertyPost?.agent_ref?.principal_agent_first_name}{" "}
                      {propertyPost?.agent_ref?.principal_agent_last_name}
                    </span>
                    <span className="date">{formattedSchedule}</span>
                  </div>
                </div>

                <div className="post-text">
                  {isEditingCaption ? (
                    <div className="edit-caption-wrapper">
                      <label className="caption-label">Post caption</label>

                      <textarea
                        className="caption-textarea"
                        value={postCaption}
                        onChange={(e) => setPostCaption(e.target.value)}
                        rows={12}
                      />

                      <div className="edit-actions-row">
                        <button
                          className="save-btn"
                          onClick={handleSaveCaption}
                        >
                          Save
                        </button>

                        <button
                          className="cancel-btn"
                          onClick={handleCancelCaption}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="post-caption-display"
                      style={{ textAlign: "left" }}
                    >
                      <span
                        className="post-content"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {isExpanded ? postCaption : shortText}
                      </span>
                      <span
                        className="see-more"
                        onClick={toggleExpand}
                        style={{
                          cursor: "pointer",
                          // color: "#007fff",
                          // fontWeight: "600",
                          // marginLeft: "4px",
                          // display: "inline-block",
                        }}
                      >
                        {isExpanded ? " See less" : " See more"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="post-preview-container" ref={imagePreviewRef}>
                  <div className="carousel-slides-wrapper">
                    <div
                      className={`carousel-slide ${
                        propertyImageIndex === 0 ? "active" : ""
                      }`}
                      // onClick={() => openImagePreviewModal(0)}
                      // style={{ cursor: "pointer" }}
                    >
                      <div className="canvas-click-wrapper">
                        <DesignPreviewRenderer
                          agencyData={commonState.agencyData as any}
                          brandData={commonState.brandData as any}
                          useAgencyOfficeLogo={commonState.useAgencyOfficeLogo}
                          designDataFromParent={
                            commonState.imageDesignData as any
                          }
                          designTemplateAssets={
                            commonState.imageTemplateAssets as any
                          }
                          canvasHeight={1080}
                          canvasWidth={1080}
                        />
                        <div
                          className="canvas-click-overlay"
                          onClick={() => openImagePreviewModal(0)}
                          role="button"
                          tabIndex={0}
                          aria-label="Open preview"
                        />
                      </div>
                    </div>

                    {filteredPropertyImages.map((img: any, idx: number) => {
                      const slideIndex = idx + 1;
                      return (
                        <div
                          key={img?._id || idx}
                          className={`carousel-slide property-image-slide ${
                            propertyImageIndex === slideIndex ? "active" : ""
                          }`}
                        >
                          <img
                            src={getPropertyImageUrl(img)}
                            alt={`Property ${slideIndex}`}
                            className="carousel-main-image"
                            loading="lazy"
                            onClick={() => openImagePreviewModal(slideIndex)}
                            style={{ cursor: "pointer" }}
                            onError={(e: any) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src =
                                "/images/image-video-generation-error-fallback-image-for-card.png";
                            }}
                          />
                        </div>
                      );
                    })}

                    {totalSlides > 1 && (
                      <>
                        <button
                          type="button"
                          className="carousel-nav-btn left"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => prevPropertyImage(e)}
                          aria-label="Previous"
                        >
                          <LeftArrowIcon />
                        </button>
                        <button
                          type="button"
                          className="carousel-nav-btn right"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => nextPropertyImage(e)}
                          aria-label="Next"
                        >
                          <RightArrowIcon />
                        </button>
                      </>
                    )}
                  </div>

                  {totalSlides > 1 && (
                    <div className="carousel-dots">
                      {Array.from({ length: totalSlides }).map((_, i) => (
                        <span
                          key={i}
                          className={`carousel-dot ${
                            i === propertyImageIndex ? "active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPropertyImageIndex(i);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* <div className="carousel-container">
                  <img
                    src={previewImages[postPreviewIndex]}
                    className="carousel-image"
                    alt="Post Preview"
                  />

                  <button className="carousel-button left" onClick={prevSlide}>
                    <LeftArrowIcon />
                  </button>

                  <button className="carousel-button right" onClick={nextSlide}>
                    <RightArrowIcon />
                  </button>

                  <div className="dots-pagination">
                    {previewImages.map((_, i) => (
                      <span
                        key={i}
                        className={`dot ${i === postPreviewIndex ? "active" : ""}`}
                        onClick={() => setPostPreviewIndex(i)}
                      />
                    ))}
                  </div>
                </div> */}
              </div>

              <div className="preview-actions-footer">
                <div className="toggle-row">
                  <span>Same caption for all the platforms</span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={sameCaptionForAll}
                      onChange={(e) => setSameCaptionForAll(e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="button-group">
                  <button className="action-btn" onClick={handleOpenCustomize}>
                    <CustomizeImageIcon /> Customize image
                  </button>
                  <button
                    className={`action-btn ${isEditingCaption ? "active" : ""}`}
                    onClick={() => setIsEditingCaption(true)}
                  >
                    <EditCaptionIcon /> Edit caption
                  </button>
                  <button
                    className="action-btn"
                    onClick={() => openImagePreviewModal()}
                  >
                    <PreviewCircleIcon /> Preview
                  </button>
                  <button
                    className="action-btn"
                    onClick={() =>
                      downloadCanvasImage(
                        imagePreviewRef,
                        `image-post-${propertyPostId}.png`,
                      )
                    }
                  >
                    <DownloadIcon /> Download
                  </button>
                </div>
              </div>
            </div>

            <div className="preview-column">
              <h3>Story post preview</h3>
              <div className="story-preview-card" ref={storyPreviewRef}>
                <div
                  className="post-preview-container story-post-preview-container"
                  onClick={() => {
                    const dataUrl = getCanvasImageFromRef(storyPreviewRef);
                    if (!dataUrl) {
                      toast.error("Preview not ready yet");
                      return;
                    }
                    setPreviewImages([dataUrl]);
                    setPostPreviewIndex(0);
                    setIsStoryPreviewOpen(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="canvas-click-wrapper">
                    <DesignPreviewRenderer
                      agencyData={commonState.agencyData as any}
                      brandData={commonState.brandData as any}
                      useAgencyOfficeLogo={commonState.useAgencyOfficeLogo}
                      designDataFromParent={commonState.storyDesignData as any}
                      designTemplateAssets={
                        commonState.storyTemplateAssets as any
                      }
                      canvasHeight={1920}
                      canvasWidth={1080}
                    />
                    <div
                      className="canvas-click-overlay"
                      onClick={() => {
                        const dataUrl = getCanvasImageFromRef(storyPreviewRef);
                        if (!dataUrl) {
                          toast.error("Preview not ready yet");
                          return;
                        }
                        setPreviewImages([dataUrl]);
                        setPostPreviewIndex(0);
                        setIsStoryPreviewOpen(true);
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Open story preview"
                    />
                  </div>
                </div>
                {/* <div className="story-carousel-wrapper">
                  <button
                    className="carousel-button left"
                    onClick={storyPrevSlide}
                  >
                    <LeftArrowIcon />
                  </button>

                  <div className="story-inner-frame">
                    <div className="story-progress-bars">
                      {storyImages.map((_, i) => {
                        let progressClass = "";

                        if (i < postStoryPreviewIndex) {
                          progressClass = "completed";
                        } else if (i === postStoryPreviewIndex) {
                          progressClass = "active";
                        }

                        return (
                          <div key={i} className="bar">
                            <div
                              key={`${i}-${postStoryPreviewIndex}`}
                              className={`progress ${progressClass}`}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="story-header">
                      <img
                        src={getImageUrl(
                          propertyPost?.agent_ref?.profile_image_ref
                            ?.compressed_file_path || "",
                        )}
                        src={
                          getAgentProfileImageUrl(
                            propertyPost?.agent_ref?.profile_image_ref,
                          ) ||
                          getImageUrl(
                            propertyPost?.agent_ref?.profile_image_ref
                              ?.compressed_file_path || "",
                          ) ||
                          "/images/profile-placeholder-image.png"
                        }
                        className="story-avatar"
                      />
                      <span className="story-user-name">
                        {propertyPost?.agent_ref?.principal_agent_first_name}{" "}
                        {propertyPost?.agent_ref?.principal_agent_last_name}
                      </span>
                      <p className="story-uploaded-time">3h</p>
                    </div>

                    <img
                      src={storyImages[postStoryPreviewIndex]}
                      className="story-main-img"
                    />

                    <div className="story-footer">
                      <div className="reply-bar">Reply to username...</div>
                    </div>
                  </div>

                  <button
                    className="carousel-button right"
                    onClick={storyNextSlide}
                  >
                    <RightArrowIcon />
                  </button>
                </div> */}
              </div>

              <div className="preview-actions-footer">
                <div className="toggle-row">
                  <span>
                    Always share story (Only for Facebook and Instagram)
                  </span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={alwaysShareStory}
                      onChange={(e) => setAlwaysShareStory(e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="button-group">
                  <button
                    className="action-btn"
                    onClick={() => {
                      const dataUrl = getCanvasImageFromRef(storyPreviewRef);
                      if (!dataUrl) {
                        toast.error("Preview not ready yet");
                        return;
                      }
                      setPreviewImages([dataUrl]);
                      setPostPreviewIndex(0);
                      setIsStoryPreviewOpen(true); // ← ye change
                    }}
                  >
                    <PreviewCircleIcon /> Preview
                  </button>
                  <button
                    className="action-btn"
                    onClick={() =>
                      downloadCanvasImage(
                        storyPreviewRef,
                        `story-post-${propertyPostId}.png`,
                      )
                    }
                  >
                    <DownloadIcon /> Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="text-config">
        <div className="post-text-header">
          <h4>Post Text</h4>
          <div className="toggle-same-text">
            <label className="switch-label">
              Same text for all the platforms
              <input
                type="checkbox"
                checked={sameTextToggle}
                onChange={() => setSameTextToggle(!sameTextToggle)}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
        <div className="platform-header">
          <div className="platform-tabs">
            {tabs.map((tab) => (
              <div
                key={tab}
                className={`platform-tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </div>
            ))}
          </div>
        </div>

        <textarea
          className="post-textarea"
          placeholder="Write your post content here..."
          defaultValue={propertyPost?.post_text || ""}
          onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
            const target = e.currentTarget;
            target.style.height = "auto";
            target.style.height = `${target.scrollHeight}px`;
          }}
        />
      </div> */}

      <div className="property-post-form">
        <div className="publish-schedule-block">
          <h3 className="section-title">Publishing schedule</h3>
          <p className="section-sub-title">
            Choose whether to publish your post immediately or schedule it for a
            future date and time
          </p>

          <div className="publish-actions">
            <button
              type="button"
              className={clsx("schedule-action-btn", {
                "schedule-action-btn--warning":
                  getDisconnectedPlatforms().length > 0,
              })}
              onClick={handlePostNowClick}
              disabled={updating}
            >
              <span>Post now</span>
              {getDisconnectedPlatforms().length > 0 && (
                <span className="schedule-action-btn__warning-icon">
                  <SocialMediaWarningIcon />
                </span>
              )}
            </button>

            <button
              type="button"
              className={clsx("schedule-action-btn", {
                "schedule-action-btn--warning":
                  getDisconnectedPlatforms().length > 0,
              })}
              onClick={handleSchedulePostClick}
              disabled={updating}
            >
              <span>Schedule post</span>
              {getDisconnectedPlatforms().length > 0 && (
                <span className="schedule-action-btn__warning-icon">
                  <SocialMediaWarningIcon />
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="save-post-preferences-row">
          <label className="save-post-preferences-left">
            <input
              type="checkbox"
              checked={savePostPreferences}
              onChange={(e) => setSavePostPreferences(e.target.checked)}
            />

            <span
              className={clsx("custom-checkbox", {
                checked: savePostPreferences,
              })}
              aria-hidden="true"
            >
              <TrueIcon />
            </span>

            <span className="save-post-preferences-text">
              Save post preferences
            </span>
          </label>

          <button
            type="button"
            className="preferences-info-btn"
            onClick={() => setPostPreferencesModalOpen(true)}
            aria-label="Post preferences info"
            title="Post preferences info"
          >
            <img src="/images/Information.svg" alt="info" />
          </button>
        </div>

        <div className="timeline-block">
          <h3 className="section-title">Post timeline</h3>
          <p className="section-sub-title">
            Published history and manage upcoming schedules
          </p>

          {timelineLoading ? (
            <div className="timeline-empty-box">
              <div className="empty-title">Loading timeline...</div>
            </div>
          ) : orderedTimelineDocs.length === 0 ? (
            <div className="timeline-empty-box">
              <div className="empty-title">Timeline is empty</div>
              <div className="empty-subtitle">
                Once you schedule or publish a post, the timeline will update
              </div>
            </div>
          ) : (
            <div className="timeline-list">
              {orderedTimelineDocs.map((t, idx) => {
                const isLast = idx === orderedTimelineDocs.length - 1;
                const published = isPublished(t);

                return (
                  <div
                    key={t?._id || idx}
                    className={clsx(
                      "timeline-item",
                      published ? "published" : "scheduled",
                    )}
                  >
                    <div
                      className={clsx("timeline-left", { "has-line": !isLast })}
                    >
                      {published ? (
                        <div className="timeline-icon timeline-icon--success">
                          <TrueIcon />
                        </div>
                      ) : (
                        <div className="timeline-icon timeline-icon--pending">
                          <img src="/images/pending-icon-2.png" alt="pending" />
                        </div>
                      )}
                    </div>

                    <div className="timeline-content">
                      <div className="timeline-pill">
                        {published
                          ? t?.schedule_type === "schedule_datetime"
                            ? "Published via Scheduled Post"
                            : "Published via Post Now"
                          : "Scheduled post"}
                      </div>

                      <div className="timeline-label">
                        {published ? "Published on" : "To be published on"}
                      </div>

                      <div className="timeline-datetime">
                        {t?.schedule_datetime
                          ? formatTimelineDateTime(t.schedule_datetime)
                          : "-"}
                      </div>

                      {!published &&
                        t?.schedule_type === "schedule_datetime" &&
                        isPending(t) &&
                        propertyPost?.schedule_type === "schedule_datetime" &&
                        propertyPost?.schedule_datetime &&
                        dayjs(propertyPost.schedule_datetime).isSame(
                          dayjs(t.schedule_datetime),
                        ) && (
                          <div className="timeline-actions">
                            <button
                              type="button"
                              className="timeline-link cancel"
                              onClick={() => setCancelScheduleModalOpen(true)}
                              disabled={updating}
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              className="timeline-link edit"
                              onClick={handleOpenEditSchedule}
                              disabled={updating}
                            >
                              Edit schedule
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="schedule-footer-actions">
          <button
            type="button"
            className="back-btn"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set("step", CommonEnums.propertyPost.property_info.key);
              setSearchParams(next);
            }}
          >
            Back
          </button>

          <button
            type="button"
            className="save-btn"
            onClick={handleSaveChanges}
            disabled={updating}
          >
            Save changes
          </button>
        </div>
      </div>

      <Modal
        open={platformPostNowOpen}
        onClose={() => setPlatformPostNowOpen(false)}
        className="property-post-now-modal"
      >
        <div className="post-now-modal-card">
          <h3 className="modal-title">Post to {activePlatformLabel} now?</h3>

          <p className="modal-desc">
            Are you sure you want to go live?
            <br />
            Your post will be published to your {activePlatformLabel} page
            instantly.
          </p>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-btn outline"
              onClick={() => setPlatformPostNowOpen(false)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-btn primary"
              disabled={updating}
              onClick={handlePlatformPostNow}
            >
              Post now
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={platformScheduleOpen}
        onClose={() => setPlatformScheduleOpen(false)}
        className="property-post-schedule-modal"
      >
        <div className="schedule-post-modal-card">
          <h3 className="modal-title modal-title--left">
            Schedule {activePlatformLabel} Post
          </h3>

          <p className="modal-desc modal-desc--left">
            Select your preferred date and time
            <br />
            Your content will be published to your {activePlatformLabel} Page
            automatically at the scheduled time
          </p>

          <div className="datetime-row plateform-datetime-row">
            <div className="datetime-field">
              <label>Date*</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  desktopModeMediaQuery="@media (min-width: 0px)"
                  value={platformScheduleDate}
                  onChange={(v) => setPlatformScheduleDate(v)}
                  format="DD/MM/YYYY"
                  slots={{
                    textField: TextField,
                    openPickerIcon: CustomCalendarIcon,
                  }}
                  disablePast
                  slotProps={{
                    openPickerButton: {
                      sx: { display: "inline-flex", opacity: 1 },
                    },
                    textField: {
                      placeholder: "DD/MM/YYYY",
                      sx: {
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          borderRadius: "8px",
                          background: "#F5F5F5",
                          "& fieldset": { border: "0px" },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="datetime-field">
              <label>Time*</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  desktopModeMediaQuery="@media (min-width: 0px)"
                  value={platformScheduleTime}
                  onChange={(v) => setPlatformScheduleTime(v)}
                  ampm
                  format="hh:mm A"
                  slots={{
                    textField: TextField,
                    openPickerIcon: CustomClockIcon,
                  }}
                  slotProps={{
                    openPickerButton: {
                      sx: { display: "inline-flex", opacity: 1 },
                    },
                    textField: {
                      placeholder: "00:00 AM",
                      sx: {
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          borderRadius: "8px",
                          background: "#F5F5F5",
                          "& fieldset": { border: "0px" },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>

          <p className="schedule-note">
            *Automatically scheduled time for peak engagement
          </p>

          <div className="modal-actions modal-actions--left">
            <button
              type="button"
              className="modal-btn outline"
              onClick={() => setPlatformScheduleOpen(false)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-btn primary"
              onClick={handlePlatformSchedule}
              disabled={updating}
            >
              Schedule
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={postNowModalOpen}
        onClose={() => setPostNowModalOpen(false)}
        className="property-post-now-modal"
      >
        <div className="post-now-modal-card">
          <h3 className="modal-title">Publish property post now?</h3>

          <p className="modal-desc">
            This will publish the property post to social media immediately.
            <br />
            Are you sure you want to proceed?
          </p>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-btn outline"
              onClick={() => setPostNowModalOpen(false)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-btn primary"
              disabled={updating}
              onClick={handlePostNow}
            >
              Post now
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={cancelScheduleModalOpen}
        onClose={() => setCancelScheduleModalOpen(false)}
        className="property-post-cancel-schedule-modal"
      >
        <div className="cancel-schedule-modal-card">
          <h3 className="modal-title">Cancel post schedule?</h3>

          <p className="modal-desc">
            This post will no longer be published at the scheduled date and time
          </p>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-btn outline"
              onClick={() => setCancelScheduleModalOpen(false)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-btn primary"
              onClick={handleConfirmCancelSchedule}
              disabled={updating}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={editScheduleModalOpen}
        onClose={() => setEditScheduleModalOpen(false)}
        className="property-post-edit-schedule-modal"
      >
        <div className="schedule-post-modal-card">
          <h3 className="modal-title modal-title--left">Edit schedule</h3>

          <p className="modal-desc modal-desc--left">
            Set the date and time for your post
            <br />
            It will be published to social media automatically at your chosen
            time
          </p>

          <div className="datetime-row">
            <div className="datetime-field">
              <label>Date*</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  desktopModeMediaQuery="@media (min-width: 0px)"
                  value={scheduleDate}
                  onChange={(newValue) => setScheduleDate(newValue)}
                  format="DD/MM/YYYY"
                  slots={{
                    textField: TextField,
                    openPickerIcon: CustomCalendarIcon,
                  }}
                  disablePast
                  slotProps={{
                    openPickerButton: {
                      sx: {
                        display: "inline-flex",
                        visibility: "visible",
                        opacity: 1,
                        flexShrink: 0,
                      },
                    },
                    textField: {
                      placeholder: "DD/MM/YYYY",
                      sx: {
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          borderRadius: "8px",
                          background: "#F5F5F5",
                          "& fieldset": { border: "0px" },
                        },

                        "& input": {
                          padding: "10px 14px",
                          fontFamily: "var(--inter)",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0.01em",
                          color: "#111111",
                        },

                        "& .MuiInputAdornment-root": {
                          order: -1,
                          marginRight: "8px",
                          marginLeft: "0px",
                          display: "flex",
                        },
                        "& .MuiIconButton-root": {
                          display: "inline-flex",
                          visibility: "visible",
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="datetime-field">
              <label>Time*</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  desktopModeMediaQuery="@media (min-width: 0px)"
                  value={scheduleTime}
                  onChange={(newValue) => setScheduleTime(newValue)}
                  ampm
                  format="hh:mm A"
                  slots={{
                    textField: TextField,
                    openPickerIcon: CustomClockIcon,
                  }}
                  slotProps={{
                    openPickerButton: {
                      sx: {
                        display: "inline-flex",
                        visibility: "visible",
                        opacity: 1,
                        flexShrink: 0,
                      },
                    },
                    textField: {
                      placeholder: "00:00 AM",
                      sx: {
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          borderRadius: "8px",
                          background: "#F5F5F5",
                          "& fieldset": { border: "0px" },
                        },
                        "& input": {
                          padding: "10px 14px",
                          fontFamily: "var(--inter)",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0.01em",
                          color: "#111111",
                        },
                        "& .MuiInputAdornment-root": {
                          order: -1,
                          marginRight: "8px",
                          marginLeft: "0px",
                          display: "flex",
                        },
                        "& .MuiIconButton-root": {
                          display: "inline-flex",
                          visibility: "visible",
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className="modal-actions modal-actions--left">
            <button
              type="button"
              className="modal-btn outline"
              onClick={() => setEditScheduleModalOpen(false)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-btn primary"
              onClick={handleConfirmSchedule}
              disabled={updating}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        className="property-post-schedule-modal"
      >
        <div className="schedule-post-modal-card">
          <h3 className="modal-title modal-title--left">Schedule post</h3>

          <p className="modal-desc modal-desc--left">
            Set the date and time for your post
            <br />
            It will be published to social media automatically at your chosen
            time
          </p>

          <div className="same-schedule-row">
            <span>Same schedule for all social media accounts</span>

            <label className="same-schedule-switch">
              <input
                type="checkbox"
                checked={sameScheduleAll}
                onChange={(e) => setSameScheduleAll(e.target.checked)}
              />
              <span className="slider" />
            </label>
          </div>

          {sameScheduleAll ? (
            <>
              <div className="datetime-row">
                <div className="datetime-field">
                  <label>Date*</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      value={scheduleDate}
                      onChange={(newValue) => setScheduleDate(newValue)}
                      format="DD/MM/YYYY"
                      slots={{
                        textField: TextField,
                        openPickerIcon: CustomCalendarIcon,
                      }}
                      disablePast
                      slotProps={{
                        openPickerButton: {
                          sx: {
                            display: "inline-flex",
                            visibility: "visible",
                            opacity: 1,
                            flexShrink: 0,
                          },
                        },
                        textField: {
                          placeholder: "DD/MM/YYYY",
                          sx: {
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                              height: 40,
                              borderRadius: "8px",
                              background: "#F5F5F5",
                              "& fieldset": { border: "0px" },
                            },
                            "& input": {
                              padding: "10px 14px",
                              fontFamily: "var(--inter)",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "100%",
                              letterSpacing: "0.01em",
                              color: "#111111",
                            },
                            "& .MuiInputAdornment-root": {
                              order: -1,
                              marginRight: "8px",
                              marginLeft: "0px",
                              display: "flex",
                            },
                            "& .MuiIconButton-root": {
                              display: "inline-flex",
                              visibility: "visible",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>

                <div className="datetime-field">
                  <label>Time*</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      value={scheduleTime}
                      onChange={(newValue) => setScheduleTime(newValue)}
                      ampm
                      format="hh:mm A"
                      slots={{
                        textField: TextField,
                        openPickerIcon: CustomClockIcon,
                      }}
                      slotProps={{
                        openPickerButton: {
                          sx: {
                            display: "inline-flex",
                            visibility: "visible",
                            opacity: 1,
                            flexShrink: 0,
                          },
                        },
                        textField: {
                          placeholder: "00:00 AM",
                          sx: {
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                              height: 40,
                              borderRadius: "8px",
                              background: "#F5F5F5",
                              "& fieldset": { border: "0px" },
                            },
                            "& input": {
                              padding: "10px 14px",
                              fontFamily: "var(--inter)",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "100%",
                              letterSpacing: "0.01em",
                              color: "#111111",
                            },
                            "& .MuiInputAdornment-root": {
                              order: -1,
                              marginRight: "8px",
                              marginLeft: "0px",
                              display: "flex",
                            },
                            "& .MuiIconButton-root": {
                              display: "inline-flex",
                              visibility: "visible",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <p className="schedule-note">
                *Automatically scheduled time for peak engagement
              </p>
            </>
          ) : (
            <>
              <div className="platform-schedule-grid">
                {SCHEDULE_PLATFORMS.map(
                  ({ key, label, Icon, supported, showNote }) => {
                    const v = perPlatformSchedule[key];

                    return (
                      <div
                        key={key}
                        className={clsx("platform-schedule-card", {
                          "platform-schedule-card--disabled":
                            supported === false,
                        })}
                      >
                        <div className="platform-schedule-card-header">
                          <span className="platform-schedule-card-icon">
                            <Icon />
                          </span>
                          <span className="platform-schedule-card-title">
                            {label}
                          </span>
                        </div>

                        <div className="platform-card-datetime platform-card-datetime--joined">
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              desktopModeMediaQuery="@media (min-width: 0px)"
                              value={v.date}
                              onChange={(newDate) =>
                                setPerPlatformSchedule((prev) => ({
                                  ...prev,
                                  [key]: { ...prev[key], date: newDate },
                                }))
                              }
                              format="DD/MM/YYYY"
                              slots={{
                                textField: TextField,
                                openPickerIcon: CustomCalendarIcon,
                              }}
                              disablePast
                              slotProps={{
                                openPickerButton: {
                                  sx: {
                                    display: "inline-flex",
                                    visibility: "visible",
                                    opacity: 1,
                                    flexShrink: 0,
                                  },
                                },
                                textField: {
                                  sx: {
                                    width: 150,
                                    "& .MuiOutlinedInput-root": {
                                      height: 40,
                                      borderRadius: "8px 0 0 8px",
                                      background: "#F5F5F5",
                                      paddingRight: "0px !important",
                                      "& fieldset": { border: "0px" },
                                    },
                                    "& input": {
                                      padding: "10px 14px",
                                      fontFamily: "var(--inter)",
                                      fontWeight: 400,
                                      fontSize: "14px",
                                      lineHeight: "100%",
                                      letterSpacing: "0.01em",
                                      color: "#111111",
                                    },
                                    "& .MuiInputAdornment-root": {
                                      order: -1,
                                      marginRight: "8px",
                                      marginLeft: "0px",
                                      display: "flex",
                                    },
                                    "& .MuiIconButton-root": {
                                      display: "inline-flex",
                                      visibility: "visible",
                                      opacity: 1,
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>

                          <div className="joined-divider" />

                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              desktopModeMediaQuery="@media (min-width: 0px)"
                              value={v.time}
                              onChange={(newTime) =>
                                setPerPlatformSchedule((prev) => ({
                                  ...prev,
                                  [key]: { ...prev[key], time: newTime },
                                }))
                              }
                              ampm
                              format="hh:mm A"
                              slots={{
                                textField: TextField,
                                openPickerIcon: CustomClockIcon,
                              }}
                              slotProps={{
                                openPickerButton: {
                                  sx: {
                                    display: "inline-flex",
                                    visibility: "visible",
                                    opacity: 1,
                                    flexShrink: 0,
                                  },
                                },
                                textField: {
                                  sx: {
                                    width: 130,
                                    "& .MuiOutlinedInput-root": {
                                      height: 40,
                                      borderRadius: "0 8px 8px 0",
                                      background: "#F5F5F5",
                                      paddingRight: "0px !important",
                                      "& fieldset": { border: "0px" },
                                    },
                                    "& input": {
                                      padding: "10px 14px",
                                      fontFamily: "var(--inter)",
                                      fontWeight: 400,
                                      fontSize: "14px",
                                      lineHeight: "100%",
                                      letterSpacing: "0.01em",
                                      color: "#111111",
                                    },
                                    "& .MuiInputAdornment-root": {
                                      order: -1,
                                      marginRight: "8px",
                                      marginLeft: "0px",
                                      display: "flex",
                                    },
                                    "& .MuiIconButton-root": {
                                      display: "inline-flex",
                                      visibility: "visible",
                                      opacity: 1,
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </div>

                        {showNote && (
                          <p className="schedule-note schedule-note--card">
                            *Automatically scheduled time for peak engagement
                          </p>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </>
          )}

          <div className="modal-actions modal-actions--left">
            <button
              type="button"
              className="modal-btn outline"
              onClick={() => setScheduleModalOpen(false)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-btn primary"
              onClick={handleConfirmSchedule}
              disabled={updating}
            >
              Schedule
            </button>
          </div>
        </div>
      </Modal>

      {/* {isUpdateTemplateOpen && propertyPostId && (
        <UpdateTemplateModal
          open={isUpdateTemplateOpen}
          onClose={() => setIsUpdateTemplateOpen(false)}
          selectedDesignTemplateId={getSelectedTemplateId()}
          onConfirm={async () => {
            try {
              setProcessingModalOpen(true);
              setIsProcessing(true);
              await PropertyApi.updatePropertyPost(propertyPostId, {
                finalize: true,
              });
              setIsProcessing(false);
              setProcessingModalOpen(false);
              setIsUpdateTemplateOpen(false);
              // window.location.reload();
              await fetchPost(true);
            } catch (err) {
              console.error("Finalize failed:", err);
              toast.error("Failed to finalize");
            }
          }}
        />
      )} */}

      <Modal
        open={processingModalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setProcessingModalOpen(false);
          }
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            {isProcessing && (
              <div className="loading-container">
                <div className="spinner" />
                <p className="loading-text">
                  Processing your request
                  <span className="desktop-break">
                    <br />
                  </span>
                  Please wait...
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={postPreferencesModalOpen}
        onClose={() => setPostPreferencesModalOpen(false)}
        className="post-preferences-modal"
      >
        <div className="post-preferences-modal-card">
          <div className="ppm-content">
            <h3 className="ppm-title">Post preferences</h3>

            <div className="ppm-section">
              <div className="ppm-section-title">Social media platforms</div>

              <div className="ppm-section-desc">
                Your selected social media accounts will be saved and
                automatically applied when you create new property posts
              </div>
            </div>

            <div className="ppm-section pp-section">
              <div className="ppm-section-title">Publishing schedule</div>

              <div className="ppm-section-desc">
                Your schedule preferences—whether you use the same timing for
                all platforms or set different times for each—will be saved for
                your next session
              </div>
            </div>

            <div className="ppm-footnote">
              *You can always review and change these settings for any
              individual post
            </div>

            <div className="ppm-actions">
              <button
                type="button"
                className="ppm-done-btn"
                onClick={() => setPostPreferencesModalOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {isStoryPreviewOpen && previewImages.length > 0 && (
        <PreviewModal
          open={isStoryPreviewOpen}
          onClose={() => setIsStoryPreviewOpen(false)}
          images={previewImages}
          initialIndex={postPreviewIndex}
        />
      )}

      {isImagePreviewOpen && previewImagesForModal.length > 0 && (
        <ImagePreviewModal
          open={isImagePreviewOpen}
          onClose={() => setIsImagePreviewOpen(false)}
          images={previewImagesForModal}
          startIndex={imagePreviewStartIndex}
        />
      )}

      {/* {isPreviewOpen && (
        <PreviewModal
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          images={
            [
              propertyPost?.image_post_design_activity_ref?.design_task_id
                ?.output_image_path
                ? CommonHelpers.getImageUrlFromImageTool({
                    path: propertyPost.image_post_design_activity_ref
                      .design_task_id.output_image_path,
                  })
                : null,
              propertyPost?.story_post_design_activity_ref?.design_task_id
                ?.output_image_path
                ? CommonHelpers.getImageUrlFromImageTool({
                    path: propertyPost.story_post_design_activity_ref
                      .design_task_id.output_image_path,
                  })
                : null,
              propertyPost?.video_post_design_activity_ref?.design_task_id
                ?.output_image_path
                ? CommonHelpers.getImageUrlFromImageTool({
                    path: propertyPost.video_post_design_activity_ref
                      .design_task_id.output_image_path,
                  })
                : null,
              propertyPost?.reel_post_design_activity_ref?.design_task_id
                ?.output_image_path
                ? CommonHelpers.getImageUrlFromImageTool({
                    path: propertyPost.reel_post_design_activity_ref
                      .design_task_id.output_image_path,
                  })
                : null,
            ].filter(Boolean) as string[]
          }
          initialIndex={postPreviewIndex}
        />
      )} */}

      <div
        className={clsx("publish-box", {
          closed: !isFloatingPublishOpen,
          "publish-box--hidden": isImagePreviewOpen || isStoryPreviewOpen,
        })}
      >
        <button
          type="button"
          className="publish-handle"
          onClick={() => setIsFloatingPublishOpen(true)}
          aria-label="Open publish actions"
        >
          <LeftArrowIcon />
        </button>

        <div className="publish-panel">
          <div className="publish-actions">
            <button
              type="button"
              className={clsx("publish-btn", {
                "publish-btn--warning": getDisconnectedPlatforms().length > 0,
              })}
              onClick={handlePostNowClick}
              disabled={updating}
            >
              <span>Post now</span>
              {getDisconnectedPlatforms().length > 0 && (
                <span className="publish-btn__warning-icon">
                  <SocialMediaWarningIcon />
                </span>
              )}
            </button>

            <button
              type="button"
              className={clsx("publish-btn", {
                "publish-btn--warning": getDisconnectedPlatforms().length > 0,
              })}
              onClick={handleSchedulePostClick}
              disabled={updating}
            >
              <span>Schedule post</span>
              {getDisconnectedPlatforms().length > 0 && (
                <span className="publish-btn__warning-icon">
                  <SocialMediaWarningIcon />
                </span>
              )}
            </button>
          </div>

          <button
            type="button"
            className="publish-collapse"
            onClick={() => setIsFloatingPublishOpen(false)}
            aria-label="Hide publish actions"
          >
            <RightArrowIcon />
          </button>
        </div>
      </div>

      <SavingProgressModal open={savingModalOpen} />
    </div>
  );
};

export default PropertyPostJourneyConfiguration;
